import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Store, Utensils, ArrowLeft, Lock, Save, X, Check, AlertCircle, Eye, EyeOff, Calendar, Paperclip, FileText, Leaf, History, Copy, PartyPopper, ChevronDown, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isRestaurantDialogOpen, setIsRestaurantDialogOpen] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [weekDays, setWeekDays] = useState([]);
  const [editingDay, setEditingDay] = useState(null);
  const [dayDishes, setDayDishes] = useState([
    { name: '', description: '', price: '' },
    { name: '', description: '', price: '' },
    { name: '', description: '', price: '' }
  ]);
  const [sortMode, setSortMode] = useState('missing-first'); // 'missing-first', 'alphabetical', 'by-region'
  const [searchQuery, setSearchQuery] = useState('');
  const [openRegions, setOpenRegions] = useState(new Set());
  const [openSections, setOpenSections] = useState(new Set(['urgent-today', 'urgent-tomorrow']));
  const [showFBPostModal, setShowFBPostModal] = useState(false);
  const [fbPostText, setFbPostText] = useState('');
  
  // État pour les suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [editingSuggestions, setEditingSuggestions] = useState(false);
  const [suggestionDishes, setSuggestionDishes] = useState([
    { name: '', description: '', price: '', is_permanent: true, date_from: '', valid_until: '' },
    { name: '', description: '', price: '', is_permanent: true, date_from: '', valid_until: '' },
    { name: '', description: '', price: '', is_permanent: true, date_from: '', valid_until: '' }
  ]);

  // État pour les événements
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', date: '', end_date: '', start_time: '19:00', end_time: '', description: '', menu: '', price: '', is_active: false, event_type: 'soiree' });
  const [eventShowDetails, setEventShowDetails] = useState(false);

  // État pour l'historique des plats
  const [dishHistory, setDishHistory] = useState([]);
  const [showFullHistory, setShowFullHistory] = useState(false);

  // État pour les pages spéciales
  const [specialPages, setSpecialPages] = useState([]);
  const [showSpecialPages, setShowSpecialPages] = useState(false);
  const [editingSpecialPage, setEditingSpecialPage] = useState(null);
  const [specialPageForm, setSpecialPageForm] = useState({ title: '', slug: '', description: '', start_date: '', end_date: '' });

  // État pour tracker les plats hérités originaux lors de l'édition d'un jour
  const [originalInheritedDishIds, setOriginalInheritedDishIds] = useState({});
  
  // Dernier restaurant modifié (pour marqueur visuel + auto-scroll)
  const [lastModifiedRestaurantId, setLastModifiedRestaurantId] = useState(
    () => sessionStorage.getItem('lastModifiedRestaurantId') || null
  );
  const lastModifiedRef = useRef(null);
  
  // État pour les dernières saisies (tous restaurants)
  const [recentDishes, setRecentDishes] = useState([]);
  const [showRecentDishes, setShowRecentDishes] = useState(true);

  // === Helpers de dates ===
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const isBefore1330 = () => {
    const now = new Date();
    return now.getHours() < 13 || (now.getHours() === 13 && now.getMinutes() < 30);
  };
  const getRelevantDate = () => getTomorrowDate();

  const getDateLabel = (dateStr) => {
    const dateObj = new Date(dateStr + 'T12:00:00');
    const dayNames = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
    return dayNames[dateObj.getDay()] + ' ' + dateObj.getDate() + '/' + (dateObj.getMonth() + 1);
  };

  // === Générateur de posts Facebook ===
  const generateFBPost = () => {
    const activeRestaurants = restaurants.filter(r => r.is_active !== false);
    const totalCount = activeRestaurants.length;
    const today = getTodayDate();
    
    // Nouveaux restaurants (< 3 jours)
    const newRestaurants = activeRestaurants.filter(r => {
      if (!r.created_at) return false;
      const created = new Date(r.created_at);
      const now = new Date();
      return Math.floor((now - created) / (1000 * 60 * 60 * 24)) <= 3;
    });
    
    // Restaurants ouverts aujourd'hui
    const todayObj = new Date(today + 'T12:00:00');
    const dayOfWeek = todayObj.getDay();
    const closedDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const openToday = activeRestaurants.filter(r => {
      if (r.temporary_closure) return false;
      if (r.vacation_start && r.vacation_end) {
        const d = new Date(today);
        if (d >= new Date(r.vacation_start) && d <= new Date(r.vacation_end)) return false;
      }
      if (r.closed_days && r.closed_days.includes(closedDayIndex)) return false;
      return true;
    });
    
    // Nombre de restaurants avec plats du jour
    const todayDishes = dishes.filter(d => !d.is_suggestion && (d.date === today || (d.valid_until && d.date <= today && d.valid_until >= today)));
    const nbWithDishes = [...new Set(todayDishes.map(d => d.restaurant_id))].length;
    
    // Jour de la semaine
    const joursSemaine = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const jourNom = joursSemaine[new Date().getDay()];
    const dayNum = new Date().getDay();
    
    // Quelques plats aléatoires
    const platsSample = todayDishes.sort(() => Math.random() - 0.5).slice(0, 3);
    const platsText = platsSample.map(d => {
      const resto = activeRestaurants.find(r => r.id === d.restaurant_id);
      return resto ? `${d.name} (${resto.name})` : d.name;
    });
    
    // Communes aléatoires pour varier
    const communes = [...new Set(activeRestaurants.map(r => r.commune).filter(Boolean))];
    const communeSample = communes.sort(() => Math.random() - 0.5).slice(0, 2);

    const n = nbWithDishes; // raccourci
    const t = totalCount;

    // ====== TOUS LES FORMATS ======
    const formats = [];
    
    // --- Catégorie: Plats du jour (chiffres) ---
    formats.push(`🍽️ Les plats de ${n} restaurants ce midi entre Genève et Lausanne !\n→ aumenu.ch`);
    formats.push(`🍴 ${n} restaurants, ${n} menus différents !\nQuel sera votre choix ce midi ?\n→ aumenu.ch`);
    formats.push(`📋 ${n} plats du jour à découvrir entre Genève et Lausanne.\nBon appétit !\n→ aumenu.ch`);
    formats.push(`🔥 ${n} restaurants jouent le jeu ce midi et vous proposent leurs plats du jour !\n→ aumenu.ch`);
    formats.push(`🍽️ Ce ${jourNom}, ${n} restaurants vous régalent entre Genève et Lausanne.\nÀ vous de choisir !\n→ aumenu.ch`);
    formats.push(`👨‍🍳 ${n} chefs cuisinent pour vous ce midi !\nDécouvrez leurs plats → aumenu.ch`);
    formats.push(`📊 ${n} plats du jour rien que pour ce midi.\nVous avez l'embarras du choix !\n→ aumenu.ch`);
    
    // --- Catégorie: Questions interactives ---
    formats.push(`🤔 Vous hésitez pour ce midi ?\nLes plats de ${n} restaurants pour vous aider à choisir !\n→ aumenu.ch`);
    formats.push(`😋 Et vous, c'est quoi votre plat du jour préféré ?\nDites-le nous en commentaire !\n→ aumenu.ch`);
    formats.push(`🗳️ Viande, poisson ou végétarien ce midi ?\nTrouvez votre bonheur parmi ${n} restaurants !\n→ aumenu.ch`);
    formats.push(`💬 Quel est votre restaurant préféré entre Genève et Lausanne ?\nDites-le nous ! En attendant, voici les plats de ${n} restaurants → aumenu.ch`);
    formats.push(`🍽️ Si vous deviez choisir un seul plat ce midi, lequel serait-ce ?\n${n} restaurants vous inspirent → aumenu.ch`);
    
    // --- Catégorie: Total / prestige ---
    formats.push(`💪 Plus de ${t} restaurants entre Genève et Lausanne sur AuMenu !\nLes plats du jour → aumenu.ch`);
    formats.push(`🌟 AuMenu, c'est plus de ${t} restaurants entre Genève et Lausanne.\nTous les plats du jour sur une seule page !\n→ aumenu.ch`);
    formats.push(`📱 Plus de ${t} restaurants à portée de clic !\nTrouvez votre plat du jour → aumenu.ch`);
    formats.push(`🗺️ De Genève à Lausanne, plus de ${t} restaurants réunis sur AuMenu.\nEt vous, vous mangez où ce midi ?\n→ aumenu.ch`);
    
    // --- Catégorie: Appels à l'action / urgence ---
    formats.push(`⏰ Bientôt midi ! Pas encore choisi votre repas ?\nLes plats de ${n} restaurants vous attendent → aumenu.ch`);
    formats.push(`🚀 Ne perdez plus de temps à chercher !\nLes plats du jour de ${n} restaurants en un clic → aumenu.ch`);
    formats.push(`📲 En 2 clics, trouvez votre plat du jour parmi ${n} restaurants.\nSimple et rapide → aumenu.ch`);
    formats.push(`🍴 Midi approche ! Les plats de ${n} restaurants vous attendent.\nFaites votre choix → aumenu.ch`);
    
    // --- Catégorie: Découverte / curiosité ---
    formats.push(`💡 Le saviez-vous ? AuMenu recense les plats du jour de plus de ${t} restaurants entre Genève et Lausanne !\n→ aumenu.ch`);
    formats.push(`🔎 Envie de découvrir un nouveau restaurant ce midi ?\n${n} plats du jour à explorer → aumenu.ch`);
    formats.push(`🎯 Marre de manger toujours la même chose ?\nDécouvrez les plats de ${n} restaurants → aumenu.ch`);
    formats.push(`✨ Chaque jour, de nouveaux plats à découvrir !\nAujourd'hui, ${n} restaurants vous inspirent → aumenu.ch`);
    formats.push(`🧭 Explorez les plats du jour entre Genève et Lausanne.\n${n} restaurants, ${n} idées pour ce midi !\n→ aumenu.ch`);
    
    // --- Catégorie: Terrasse / saison ---
    formats.push(`☀️ Un plat du jour en terrasse, ça vous dit ?\nLes plats de ${n} restaurants → aumenu.ch`);
    formats.push(`🌿 C'est la saison des belles terrasses !\nDécouvrez les plats de ${n} restaurants entre Genève et Lausanne → aumenu.ch`);
    formats.push(`🌞 Profitez du beau temps pour découvrir un nouveau restaurant !\n${n} plats du jour → aumenu.ch`);
    
    // --- Catégorie: Jours spéciaux ---
    if (dayNum === 1) {
      formats.push(`💪 Bon lundi à tous ! C'est reparti !\nLes plats de ${n} restaurants pour bien démarrer la semaine → aumenu.ch`);
      formats.push(`🌅 Nouvelle semaine, nouveaux plats !\n${n} restaurants vous proposent leurs plats du jour → aumenu.ch`);
    }
    if (dayNum === 2) {
      formats.push(`😊 C'est mardi ! La semaine est bien lancée.\nLes plats de ${n} restaurants pour ce midi → aumenu.ch`);
    }
    if (dayNum === 3) {
      formats.push(`🐪 C'est mercredi, on est à la moitié de la semaine !\nRécompensez-vous avec un bon plat du jour → aumenu.ch`);
    }
    if (dayNum === 4) {
      formats.push(`😁 Plus qu'un jour avant le weekend !\nEn attendant, les plats de ${n} restaurants → aumenu.ch`);
    }
    if (dayNum === 5) {
      formats.push(`🎉 C'est vendredi ! On se fait plaisir ce midi ?\nLes plats de ${n} restaurants → aumenu.ch`);
      formats.push(`🥂 Vendredi = plaisir ! Trouvez LE plat du jour qui vous fera sourire.\n${n} restaurants → aumenu.ch`);
    }
    
    // --- Catégorie: Plats exemples ---
    if (platsText.length >= 3) {
      formats.push(`😋 Au menu ce midi :\n• ${platsText[0]}\n• ${platsText[1]}\n• ${platsText[2]}\nEt bien d'autres → aumenu.ch`);
    }
    if (platsText.length >= 2) {
      formats.push(`🍽️ Quelques idées pour ce midi :\n• ${platsText[0]}\n• ${platsText[1]}\nDécouvrez tous les plats → aumenu.ch`);
    }
    if (platsText.length >= 1) {
      formats.push(`👀 Ce midi par exemple :\n${platsText[0]}\nEt ${n - 1} autres restaurants → aumenu.ch`);
    }
    
    // --- Catégorie: Communauté / partage ---
    formats.push(`👥 Partagez cette page avec vos collègues !\nLes plats de ${n} restaurants ce midi → aumenu.ch`);
    formats.push(`📢 Connaissez-vous AuMenu ? Plus de ${t} restaurants entre Genève et Lausanne, leurs plats du jour chaque midi.\n→ aumenu.ch`);
    formats.push(`❤️ Vous aimez bien manger le midi ? Nous aussi !\nLes plats de ${n} restaurants → aumenu.ch`);
    formats.push(`🤝 Partagez vos bonnes adresses ! Et en attendant, les plats de ${n} restaurants → aumenu.ch`);
    
    // --- Catégorie: Par commune ---
    if (communeSample.length >= 2) {
      formats.push(`📍 Que vous soyez à ${communeSample[0]} ou ${communeSample[1]}, il y a un plat du jour qui vous attend !\n→ aumenu.ch`);
    }
    if (communeSample.length >= 1) {
      formats.push(`📍 Du côté de ${communeSample[0]} ce midi ? Découvrez les plats du jour des restaurants du coin !\n→ aumenu.ch`);
    }
    
    // --- Catégorie: Nouveaux restaurants (≥ 2 seulement) ---
    if (newRestaurants.length >= 2) {
      formats.push(`🆕 ${newRestaurants.length} nouveaux restaurants de plus sur AuMenu !\nDécouvrez-les → aumenu.ch`);
      formats.push(`🎉 AuMenu grandit ! ${newRestaurants.length} nouveaux restaurants nous ont rejoints.\nDécouvrez leurs plats → aumenu.ch`);
    }

    // Choisir un format aléatoire
    const randomIndex = Math.floor(Math.random() * formats.length);
    return formats[randomIndex];
  };

  // Statut d'un restaurant pour une date donnée
  const getDishStatusForDate = (restaurantId, targetDate, closedDays = [], temporaryClosure = false, vacationStart = null, vacationEnd = null, suggestionsOnly = false, hasDailyDish = false) => {
    const dateObj = new Date(targetDate + 'T12:00:00');
    const dayOfWeek = dateObj.getDay();
    const closedDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    if (temporaryClosure) return 'temporary';
    if (vacationStart && vacationEnd) {
      const d = new Date(targetDate);
      if (d >= new Date(vacationStart) && d <= new Date(vacationEnd)) return 'vacation';
    }
    if (closedDays && closedDays.includes(closedDayIndex)) return 'closed';
    
    // Plat du jour exact (date == targetDate, sans valid_until)
    const hasDailyDishForDate = dishes.some(d => d.restaurant_id === restaurantId && !d.is_suggestion && d.date === targetDate && !d.valid_until);
    // Plat avec valid_until couvrant la date
    const hasWeekDish = dishes.some(d => d.restaurant_id === restaurantId && !d.is_suggestion && d.valid_until && d.date <= targetDate && d.valid_until >= targetDate);
    
    // Si le restaurant a un plat du jour quotidien obligatoire
    if (hasDailyDish) {
      // Il faut un plat du jour exact EN PLUS des éventuels plats de la semaine
      if (hasDailyDishForDate) return 'ok';
      // Il a des plats de la semaine mais pas de plat du jour → à contacter
      if (hasWeekDish) return 'missing_daily';
      // Rien du tout
    } else {
      // Comportement normal : n'importe quel plat suffit
      if (hasDailyDishForDate || hasWeekDish) return 'ok';
    }
    
    // Si le restaurant est marqué "suggestions uniquement", les suggestions suffisent
    const hasSuggestion = suggestions.some(s => s.restaurant_id === restaurantId);
    if (suggestionsOnly && hasSuggestion) return 'ok';
    // Sinon, les suggestions ne suffisent pas
    const hasSuggestionNoDate = suggestions.some(s => s.restaurant_id === restaurantId && !s.valid_until);
    if (hasSuggestionNoDate) return 'suggestion_only';
    const hasSuggestionWithDate = suggestions.some(s => s.restaurant_id === restaurantId && s.valid_until);
    if (hasSuggestionWithDate) return 'suggestion_only';
    return 'missing';
  };

  // Backward compat pour la vue détail
  const hasRelevantDish = (restaurantId, closedDays = [], temporaryClosure = false, vacationStart = null, vacationEnd = null, suggestionsOnly = false, hasDailyDish = false) => {
    const relevantDate = getRelevantDate();
    const status = getDishStatusForDate(restaurantId, relevantDate, closedDays, temporaryClosure, vacationStart, vacationEnd, suggestionsOnly, hasDailyDish);
    return { status, date: relevantDate };
  };

  // Vérifier le statut de la semaine (fenêtre glissante de 7 jours à partir d'aujourd'hui)
  const getWeekStatus = (restaurantId, closedDays = [], temporaryClosure = false, vacationStart = null, vacationEnd = null) => {
    // Si fermeture temporaire ou vacances, la semaine est "ok"
    if (temporaryClosure) return { status: 'temporary' };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let missingDays = 0;
    let totalOpenDays = 0;
    
    // Vérifier les 7 prochains jours à partir d'aujourd'hui
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Index du jour dans la semaine (0=lundi, ..., 6=dimanche)
      const dayOfWeek = date.getDay(); // 0=dimanche, 1=lundi, ..., 6=samedi
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      // Vérifier si en vacances ce jour
      if (vacationStart && vacationEnd) {
        const start = new Date(vacationStart);
        const end = new Date(vacationEnd);
        if (date >= start && date <= end) {
          continue; // Jour de vacances, on ne compte pas
        }
      }
      
      // Vérifier si jour fermé
      if (closedDays && closedDays.includes(dayIndex)) {
        continue; // Jour fermé, on ne compte pas
      }
      
      totalOpenDays++;
      
      // Vérifier s'il y a un plat pour ce jour (direct ou hérité via valid_until)
      const hasDish = dishes.some(d => d.restaurant_id === restaurantId && (d.date === dateStr || (d.valid_until && d.date <= dateStr && d.valid_until >= dateStr)));
      if (!hasDish) {
        missingDays++;
      }
    }
    
    if (totalOpenDays === 0) {
      return { status: 'closed', missingDays: 0, totalOpenDays: 0 };
    }
    
    return { 
      status: missingDays === 0 ? 'ok' : 'missing', 
      missingDays, 
      totalOpenDays 
    };
  };

  // Toggle restaurant active status
  const toggleRestaurantActive = async (e, restaurant) => {
    e.stopPropagation(); // Empêcher l'ouverture du détail
    try {
      const newStatus = !restaurant.is_active;
      await axios.put(`${API}/restaurants/${restaurant.id}`, { is_active: newStatus });
      toast.success(newStatus ? 'Restaurant activé' : 'Restaurant désactivé');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  useEffect(() => {
    const days = generateWeekDays();
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadData(days);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await axios.post(`${API}/admin/verify`, { password });
      if (response.data.success) {
        sessionStorage.setItem('adminAuth', 'true');
        setIsAuthenticated(true);
        setLoading(true);
        loadData();
      } else {
        setAuthError('Mot de passe incorrect');
      }
    } catch (error) {
      setAuthError('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setPassword('');
  };

  // Générer les jours (fenêtre glissante de 14 jours)
  // Après 13h30, on commence à demain (le service de midi est terminé)
  const generateWeekDays = (closedDays = []) => {
    const days = [];
    const now = new Date();
    const after1330 = now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30);
    const startOffset = after1330 ? 1 : 0;
    
    for (let i = startOffset; i < startOffset + 14; i++) {
      const date = new Date(now);
      date.setDate(now.getDate() + i);
      
      const dayOfWeek = date.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      if (!closedDays.includes(dayIndex)) {
        days.push({
          date: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }),
          dayIndex: dayIndex,
          isToday: i === 0,
          isNextWeek: i >= 7
        });
      }
    }
    
    setWeekDays(days);
    return days;
  };

  const loadData = async (daysToLoad = null) => {
    try {
      // Charger les restaurants (incluant les inactifs pour l'admin)
      const restaurantsRes = await axios.get(`${API}/restaurants?include_inactive=true`);
      const normalizedRestaurants = restaurantsRes.data.map(r => ({
        ...r, 
        id: r._id || r.id,
        is_active: r.is_active !== false // Par défaut actif si non défini
      }));
      setRestaurants(normalizedRestaurants.sort((a, b) => a.name.localeCompare(b.name)));
      
      // Utiliser les jours passés ou générer les dates
      const days = daysToLoad || [];
      if (days.length === 0) {
        const today = new Date();
        for (let i = 0; i < 14; i++) {  // 14 jours pour couvrir 2 semaines
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          days.push({ date: date.toISOString().split('T')[0] });
        }
      }
      
      // Charger les plats pour chaque jour (avec suggestions expirées pour l'admin)
      const dishPromises = days.map(day => 
        axios.get(`${API}/dishes?date=${day.date}&include_expired=true`).catch(() => ({ data: [] }))
      );
      
      const dishResponses = await Promise.all(dishPromises);
      const allDishes = dishResponses.flatMap(res => res.data);
      // Dédupliquer par ID
      const uniqueDishes = [];
      const seenIds = new Set();
      for (const d of allDishes) {
        const id = d._id || d.id;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          uniqueDishes.push(d);
        }
      }
      const normalizedDishes = uniqueDishes
        .filter(d => !d.is_suggestion) // Exclure les suggestions des plats quotidiens
        .map(d => ({...d, id: d._id || d.id}));
      setDishes(normalizedDishes);
      
      // Charger les suggestions séparément (seulement les actives + permanentes)
      const today = new Date().toISOString().split('T')[0];
      const suggestionsAll = uniqueDishes
        .filter(d => d.is_suggestion)
        .filter(d => !d.valid_until || d.valid_until >= today)
        .map(d => ({...d, id: d._id || d.id}));
      setSuggestions(suggestionsAll);
      console.log('Plats chargés:', normalizedDishes.length);

      // Charger les événements
      try {
        const eventsRes = await axios.get(`${API}/events`);
        setEvents(eventsRes.data.map(e => ({...e, id: e._id || e.id})));
      } catch (e) { console.warn('Events non chargés:', e); }

      // Charger les pages spéciales
      try {
        const spRes = await axios.get(`${API}/special-pages`);
        setSpecialPages(spRes.data.map(p => ({...p, id: p._id || p.id})));
      } catch (e) { console.warn('Pages spéciales non chargées:', e); }

      // Charger les dernières saisies (tous restaurants)
      try {
        const recentRes = await axios.get(`${API}/dishes/recent?limit=30`);
        setRecentDishes(recentRes.data);
      } catch (e) { console.warn('Plats récents non chargés:', e); }
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRestaurant = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Récupérer les jours de fermeture cochés
    const closedDaysValues = formData.getAll('closed_days');
    const closedDays = closedDaysValues.map(v => parseInt(v));
    
    const restaurantData = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      address: formData.get('address'),
      commune: formData.get('commune') || null,
      region: formData.get('region') || null,
      lat: parseFloat(formData.get('lat')) || 0,
      lng: parseFloat(formData.get('lng')) || 0,
      website: formData.get('website') || null,
      menu_url: formData.get('menu_url') || null,
      closed_days: closedDays.length > 0 ? closedDays : null,
      temporary_closure: formData.get('temporary_closure') === 'on',
      vacation_start: formData.get('vacation_start') || null,
      vacation_end: formData.get('vacation_end') || null,
      vacation_reason: formData.get('vacation_reason') || null,
      menu_note: formData.get('menu_note') || null,
      dogs_allowed: formData.get('dogs_allowed') || null,
      vegetarian_menu: formData.get('vegetarian_menu') === 'on',
      takeaway: formData.get('takeaway') === 'on',
      suggestions_only: formData.get('suggestions_only') === 'on',
      has_daily_dish: formData.get('has_daily_dish') === 'on',
      show_as_new: formData.get('show_as_new') === 'on',
    };

    try {
      if (editingRestaurant) {
        await axios.put(`${API}/restaurants/${editingRestaurant.id}`, restaurantData);
        toast.success('Restaurant modifié');
        
        // Mettre à jour le restaurant sélectionné avec les nouvelles données
        if (selectedRestaurant && editingRestaurant.id === selectedRestaurant.id) {
          setSelectedRestaurant({
            ...selectedRestaurant,
            ...restaurantData,
            id: selectedRestaurant.id,
            closed_days: closedDays.length > 0 ? closedDays : null
          });
          generateWeekDays(closedDays.length > 0 ? closedDays : []);
        }
      } else {
        await axios.post(`${API}/restaurants`, restaurantData);
        toast.success('Restaurant ajouté');
      }
      await loadData();
      setIsRestaurantDialogOpen(false);
      setEditingRestaurant(null);
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteRestaurant = async (id) => {
    if (!window.confirm('Supprimer ce restaurant et tous ses plats ?')) return;
    try {
      await axios.delete(`${API}/restaurants/${id}`);
      toast.success('Restaurant supprimé');
      setSelectedRestaurant(null);
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Obtenir les plats du jour pour une date
  const getDishesForDay = (restaurantId, date) => {
    return dishes.filter(d => 
      d.restaurant_id === restaurantId && 
      d.date === date
    );
  };

  // Retourne les plats effectifs pour un jour : plats directs + plats hérités (via valid_until), dédupliqués
  const getEffectiveDishesForDay = (restaurantId, date) => {
    const directDishes = dishes.filter(d => 
      d.restaurant_id === restaurantId && d.date === date
    ).map(d => ({ ...d, isInherited: false }));
    
    const inheritedDishes = dishes.filter(d =>
      d.restaurant_id === restaurantId &&
      d.date < date &&
      d.valid_until &&
      d.valid_until >= date &&
      !(d.excluded_dates && d.excluded_dates.includes(date))
    ).map(d => ({ ...d, isInherited: true }));
    
    // Dédupliquer par nom (les plats directs ont la priorité)
    const seenNames = new Set(directDishes.map(d => d.name.toLowerCase()));
    const uniqueInherited = inheritedDishes.filter(d => !seenNames.has(d.name.toLowerCase()));
    
    // Ordre : plats directs (sans valid_until d'abord, puis avec), puis hérités
    const sortedDirect = [...directDishes].sort((a, b) => {
      if (!a.valid_until && b.valid_until) return -1;
      if (a.valid_until && !b.valid_until) return 1;
      return 0;
    });
    
    return [...sortedDirect, ...uniqueInherited];
  };

  // Obtenir les suggestions actives d'un restaurant (exclure les expirées)
  const getSuggestionsForRestaurant = (restaurantId) => {
    const today = new Date().toISOString().split('T')[0];
    return suggestions.filter(d => {
      if (d.restaurant_id !== restaurantId) return false;
      // Garder si permanent (pas de valid_until)
      if (!d.valid_until) return true;
      // Garder si encore actif (valid_until >= aujourd'hui)
      return d.valid_until >= today;
    });
  };

  // Charger l'historique des plats d'un restaurant
  const loadDishHistory = async (restaurantId) => {
    try {
      const res = await axios.get(`${API}/dishes/history/${restaurantId}?weeks=4`);
      setDishHistory(res.data);
      setShowFullHistory(false);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setDishHistory([]);
    }
  };

  // Charger les dernières saisies (tous restaurants confondus)
  const loadRecentDishes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dishes/recent?limit=30`);
      setRecentDishes(res.data);
    } catch (error) {
      console.error('Erreur chargement plats récents:', error);
    }
  }, []);

  // Copier un plat historique dans le formulaire du jour
  const copyHistoryDish = (dish) => {
    if (!editingDay) {
      toast.error('Ouvrez d\'abord un jour pour y copier un plat');
      return;
    }
    const emptyIndex = dayDishes.findIndex(d => !d.name);
    const targetIndex = emptyIndex >= 0 ? emptyIndex : 0;
    const newDishes = [...dayDishes];
    newDishes[targetIndex] = {
      ...newDishes[targetIndex],
      name: dish.name,
      description: dish.description || '',
      price: dish.price?.toString() || '',
      is_vegetarian: dish.is_vegetarian || false
    };
    setDayDishes(newDishes);
    toast.success(`"${dish.name}" copié dans le plat ${targetIndex + 1}`);
  };

  const openDayEditor = (day) => {
    const effectiveDishes = getEffectiveDishesForDay(selectedRestaurant.id, day.date);
    
    // Stocker les IDs des plats hérités pour pouvoir les exclure à la sauvegarde
    const inheritedIds = {};
    [0, 1, 2].forEach(i => {
      if (effectiveDishes[i]?.isInherited) {
        inheritedIds[i] = effectiveDishes[i]?.id || effectiveDishes[i]?._id;
      }
    });
    setOriginalInheritedDishIds(inheritedIds);
    
    // Auto-remplir "Plats à la carte" pour samedi (5) et dimanche (6) si pas de plats existants
    const dayOfWeek = new Date(day.date + 'T12:00:00').getDay(); // 0=dim, 6=sam
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const closedDays = selectedRestaurant.closed_days || [];
    const isOpenOnThisDay = !closedDays.includes(dayOfWeek === 0 ? 6 : dayOfWeek - 1); // conversion JS→ISO
    const shouldAutoFill = isWeekend && isOpenOnThisDay && effectiveDishes.length === 0;
    
    const dishesData = [0, 1, 2].map(i => ({
      id: effectiveDishes[i]?.isInherited ? undefined : effectiveDishes[i]?.id,
      name: effectiveDishes[i]?.name || (i === 0 && shouldAutoFill ? 'Plats à la carte' : ''),
      description: effectiveDishes[i]?.description || '',
      price: effectiveDishes[i]?.price?.toString() || '',
      valid_until: effectiveDishes[i]?.isInherited ? '' : (effectiveDishes[i]?.valid_until || ''),
      is_vegetarian: effectiveDishes[i]?.is_vegetarian || false,
      is_menu: effectiveDishes[i]?.is_menu || false,
      is_evening: effectiveDishes[i]?.is_evening || false,
      is_both_services: effectiveDishes[i]?.is_both_services || false,
      is_centered: effectiveDishes[i]?.is_centered || false,
      is_title_centered: effectiveDishes[i]?.is_title_centered || effectiveDishes[i]?.is_centered || false,
      is_description_centered: effectiveDishes[i]?.is_description_centered || effectiveDishes[i]?.is_centered || false,
      flyer_url: effectiveDishes[i]?.flyer_url || null,
      isInherited: effectiveDishes[i]?.isInherited || false
    }));
    setDayDishes(dishesData);
    setEditingDay(day);
  };

  const closeDayEditor = () => {
    setEditingDay(null);
    setOriginalInheritedDishIds({});
    setDayDishes([
      { name: '', description: '', price: '' },
      { name: '', description: '', price: '' },
      { name: '', description: '', price: '' }
    ]);
  };

  const updateDayDish = (index, field, value) => {
    const newDishes = [...dayDishes];
    newDishes[index] = { ...newDishes[index], [field]: value };
    // Quand on vide le nom, remettre à zéro valid_until et marquer comme non-hérité
    if (field === 'name' && !value) {
      newDishes[index].valid_until = '';
      newDishes[index].isInherited = false;
    }
    // Dès qu'on modifie un plat hérité, il devient un plat direct
    if (newDishes[index].isInherited && field !== 'isInherited') {
      newDishes[index].isInherited = false;
    }
    setDayDishes(newDishes);
  };

  const saveDayDishes = async () => {
    const savedDate = editingDay.date;
    try {
      // Exclure la date des plats hérités qui ont été modifiés ou supprimés
      for (const [index, dishId] of Object.entries(originalInheritedDishIds)) {
        const currentDish = dayDishes[parseInt(index)];
        // Si le plat hérité a été vidé (supprimé) ou modifié (isInherited passé à false)
        if (!currentDish?.name || !currentDish?.isInherited) {
          try {
            await axios.post(`${API}/dishes/${dishId}/exclude-date`, { date: editingDay.date });
          } catch (err) {
            console.error('Erreur exclusion date:', err);
          }
        }
      }

      const existingDishes = getDishesForDay(selectedRestaurant.id, editingDay.date);
      const existingIds = new Set(existingDishes.map(d => d.id));
      const keptIds = new Set();

      const dishPayload = (dish) => ({
        name: dish.name,
        description: dish.description,
        restaurant_id: selectedRestaurant.id,
        date: editingDay.date,
        price: dish.price ? parseFloat(dish.price) : 0,
        valid_until: dish.valid_until || null,
        is_vegetarian: dish.is_vegetarian || false,
        is_menu: dish.is_menu || false,
        is_evening: dish.is_evening || false,
        is_both_services: dish.is_both_services || false,
        is_centered: dish.is_centered || false,
        is_title_centered: dish.is_title_centered || false,
        is_description_centered: dish.is_description_centered || false
      });

      for (const dish of dayDishes) {
        if (!dish.name) continue;
        if (dish.isInherited) continue; // Ne pas re-créer les plats hérités (valid_until d'un autre jour)
        if (dish.id && existingIds.has(dish.id)) {
          await axios.put(`${API}/dishes/${dish.id}`, dishPayload(dish));
          keptIds.add(dish.id);
        } else {
          await axios.post(`${API}/dishes`, dishPayload(dish));
        }
      }

      // Supprimer uniquement les plats retirés
      for (const existing of existingDishes) {
        if (!keptIds.has(existing.id)) {
          await axios.delete(`${API}/dishes/${existing.id}`);
        }
      }

      toast.success('Plats du jour enregistrés');
      // Tracker le dernier restaurant modifié
      setLastModifiedRestaurantId(selectedRestaurant.id);
      sessionStorage.setItem('lastModifiedRestaurantId', selectedRestaurant.id);
      await loadData();
      closeDayEditor();
      setTimeout(() => {
        const el = document.querySelector(`[data-day-date="${savedDate}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const openRestaurantDialog = (restaurant = null) => {
    setEditingRestaurant(restaurant);
    setIsRestaurantDialogOpen(true);
  };

  // === Gestion des suggestions ===
  const openSuggestionsEditor = () => {
    const existing = getSuggestionsForRestaurant(selectedRestaurant.id);
    const data = [0, 1, 2].map(i => ({
      id: existing[i]?.id || undefined,
      name: existing[i]?.name || '',
      description: existing[i]?.description || '',
      price: existing[i]?.price?.toString() || '',
      is_vegetarian: existing[i]?.is_vegetarian || false,
      is_permanent: existing[i] ? !existing[i]?.valid_until : true,
      date_from: existing[i]?.date || '',
      valid_until: existing[i]?.valid_until || '',
      display_days: existing[i]?.display_days || []
    }));
    setSuggestionDishes(data);
    setEditingSuggestions(true);
  };

  const closeSuggestionsEditor = () => {
    setEditingSuggestions(false);
    setSuggestionDishes([
      { name: '', description: '', price: '', is_vegetarian: false, is_permanent: true, date_from: '', valid_until: '', display_days: [] },
      { name: '', description: '', price: '', is_vegetarian: false, is_permanent: true, date_from: '', valid_until: '', display_days: [] },
      { name: '', description: '', price: '', is_vegetarian: false, is_permanent: true, date_from: '', valid_until: '', display_days: [] }
    ]);
  };

  const updateSuggestionDish = (index, field, value) => {
    const newDishes = [...suggestionDishes];
    newDishes[index] = { ...newDishes[index], [field]: value };
    setSuggestionDishes(newDishes);
  };

  const saveSuggestions = async () => {
    try {
      // Supprimer les anciennes suggestions
      const existing = getSuggestionsForRestaurant(selectedRestaurant.id);
      for (const s of existing) {
        await axios.delete(`${API}/dishes/${s.id}`);
      }
      // Créer les nouvelles
      const today = new Date().toISOString().split('T')[0];
      for (const dish of suggestionDishes) {
        if (dish.name) {
          await axios.post(`${API}/dishes`, {
            name: dish.name,
            description: dish.description,
            restaurant_id: selectedRestaurant.id,
            date: dish.is_permanent ? today : (dish.date_from || today),
            price: dish.price ? parseFloat(dish.price) : 0,
            is_suggestion: true,
            is_vegetarian: dish.is_vegetarian || false,
            valid_until: dish.is_permanent ? null : (dish.valid_until || null),
            display_days: dish.is_permanent && dish.display_days?.length > 0 ? dish.display_days : null
          });
        }
      }
      toast.success('Suggestions enregistrées');
      await loadData();
      closeSuggestionsEditor();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // === Gestion des événements ===
  const getEventsForRestaurant = (restaurantId) => {
    return events.filter(e => e.restaurant_id === restaurantId);
  };

  const openEventEditor = (event = null) => {
    if (event) {
      setEventForm({
        title: event.title || '',
        date: event.date || '',
        end_date: event.end_date || '',
        start_time: event.start_time || '19:00',
        end_time: event.end_time || '',
        description: event.description || '',
        menu: event.menu || '',
        price: event.price?.toString() || '',
        is_title_centered: event.is_title_centered || false,
        is_menu_centered: event.is_menu_centered || false,
        is_active: event.is_active !== false,
        event_type: event.event_type || 'classic'
      });
      setEventShowDetails(!!(event.end_date || event.start_time || event.end_time || event.description || event.menu));
      setEditingEvent(event);
    } else {
      setEventForm({ title: '', date: '', end_date: '', start_time: '19:00', end_time: '', description: '', menu: '', price: '', is_title_centered: false, is_menu_centered: false, is_active: false, event_type: 'soiree' });
      setEventShowDetails(false);
      setEditingEvent('new');
    }
  };

  const closeEventEditor = () => {
    setEditingEvent(null);
    setEventShowDetails(false);
    setEventForm({ title: '', date: '', end_date: '', start_time: '19:00', end_time: '', description: '', menu: '', price: '', is_title_centered: false, is_menu_centered: false, is_active: false, event_type: 'soiree' });
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.date) {
      toast.error('Titre et date requis');
      return;
    }
    try {
      const payload = {
        restaurant_id: selectedRestaurant.id,
        title: eventForm.title,
        date: eventForm.date,
        end_date: eventForm.end_date || null,
        start_time: eventForm.start_time || null,
        end_time: eventForm.end_time || null,
        description: eventForm.description || null,
        menu: eventForm.menu || null,
        price: eventForm.price ? parseFloat(eventForm.price) : null,
        is_title_centered: eventForm.is_title_centered || false,
        is_menu_centered: eventForm.is_menu_centered || false,
        is_active: eventForm.is_active || false,
        event_type: eventForm.event_type || 'soiree'
      };
      if (editingEvent && editingEvent !== 'new') {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success('Événement modifié');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Événement créé');
      }
      await loadData();
      closeEventEditor();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Événement supprimé');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const uploadFlyer = async (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/events/${eventId}/flyer`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Flyer uploadé');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    }
  };

  const deleteFlyer = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/flyer`);
      toast.success('Flyer supprimé');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression du flyer');
    }
  };

  // === Gestion des Pages Spéciales ===
  const openSpecialPageEditor = (page = null) => {
    if (page) {
      setSpecialPageForm({
        title: page.title || '',
        slug: page.slug || '',
        description: page.description || '',
        start_date: page.start_date || '',
        end_date: page.end_date || ''
      });
      setEditingSpecialPage(page);
    } else {
      setSpecialPageForm({ title: '', slug: '', description: '', start_date: '', end_date: '' });
      setEditingSpecialPage('new');
    }
  };

  const closeSpecialPageEditor = () => {
    setEditingSpecialPage(null);
    setSpecialPageForm({ title: '', slug: '', description: '', start_date: '', end_date: '' });
  };

  const saveSpecialPage = async () => {
    if (!specialPageForm.title || !specialPageForm.start_date || !specialPageForm.end_date) {
      toast.error('Titre, date de début et date de fin requis');
      return;
    }
    try {
      const payload = {
        title: specialPageForm.title,
        slug: specialPageForm.slug || undefined,
        description: specialPageForm.description || undefined,
        start_date: specialPageForm.start_date,
        end_date: specialPageForm.end_date
      };
      if (editingSpecialPage && editingSpecialPage !== 'new') {
        await axios.put(`${API}/special-pages/${editingSpecialPage.id}`, payload);
        toast.success('Page spéciale modifiée');
      } else {
        await axios.post(`${API}/special-pages`, payload);
        toast.success('Page spéciale créée');
      }
      await loadData();
      closeSpecialPageEditor();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const deleteSpecialPage = async (pageId) => {
    if (!window.confirm('Supprimer cette page spéciale ?')) return;
    try {
      await axios.delete(`${API}/special-pages/${pageId}`);
      toast.success('Page spéciale supprimée');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSpecialPageActive = async (page) => {
    try {
      await axios.put(`${API}/special-pages/${page.id}`, { is_active: !page.is_active });
      toast.success(page.is_active ? 'Page désactivée' : 'Page activée');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const uploadSpecialPageBanner = async (pageId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/special-pages/${pageId}/banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Bannière uploadée');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload de la bannière');
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Lock className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-xl">Administration</CardTitle>
            <p className="text-sm text-stone-500">Entrez le mot de passe pour accéder</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {authError && <p className="text-sm text-red-600">{authError}</p>}
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">Connexion</Button>
            </form>
            <div className="mt-4 text-center">
              <a href="/" className="text-sm text-stone-500 hover:text-amber-600">← Retour au site</a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-600">Chargement...</div>
      </div>
    );
  }

  // Restaurant detail view with weekly dishes
  if (selectedRestaurant) {
    return (
      <div className="min-h-screen bg-stone-50">
        <header className="bg-white border-b border-stone-200 shadow-sm sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => { setSelectedRestaurant(null); setEditingDay(null); setEditingEvent(null); setEditingSuggestions(false); }}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-xl font-bold text-amber-700">{selectedRestaurant.name}</h1>
                  <p className="text-sm text-stone-500">{selectedRestaurant.phone} • {selectedRestaurant.address}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openRestaurantDialog(selectedRestaurant)}>
                  <Pencil className="h-4 w-4 mr-1" /> Modifier
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteRestaurant(selectedRestaurant.id)}>
                  <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6">
          {/* === Section Suggestions === */}
          <Card className="mb-6 border-amber-200 bg-amber-50/30">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-amber-700 flex items-center gap-2">
                  <Utensils className="h-4 w-4" /> Suggestions du moment
                </CardTitle>
                {!editingSuggestions && (
                  <Button variant="outline" size="sm" onClick={openSuggestionsEditor}>
                    <Pencil className="h-3 w-3 mr-1" /> {getSuggestionsForRestaurant(selectedRestaurant.id).length > 0 ? 'Modifier' : 'Ajouter'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="py-3 px-4 pt-0">
              {editingSuggestions ? (
                <div className="space-y-4">
                  {[0, 1, 2].map(index => (
                    <div key={`suggestion-${index}`} className="p-3 bg-white rounded-lg border border-amber-200">
                      <p className="text-xs font-semibold text-amber-700 mb-2">Suggestion {index + 1}</p>
                      <div className="grid gap-2">
                        <Input
                          placeholder="Nom du plat"
                          value={suggestionDishes[index]?.name || ''}
                          onChange={(e) => updateSuggestionDish(index, 'name', e.target.value)}
                          autoComplete="off"
                          name={`suggestion-name-${index}`}
                        />
                        <Input
                          placeholder="Description / accompagnements"
                          value={suggestionDishes[index]?.description || ''}
                          onChange={(e) => updateSuggestionDish(index, 'description', e.target.value)}
                          autoComplete="off"
                          name={`suggestion-desc-${index}`}
                        />
                        <Input
                          placeholder="Prix (CHF)"
                          type="number"
                          step="0.5"
                          value={suggestionDishes[index]?.price || ''}
                          onChange={(e) => updateSuggestionDish(index, 'price', e.target.value)}
                          autoComplete="off"
                          name={`suggestion-price-${index}`}
                        />
                        <div className="flex flex-col gap-2 mt-1">
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                            <input
                              type="checkbox"
                              checked={suggestionDishes[index]?.is_vegetarian || false}
                              onChange={(e) => updateSuggestionDish(index, 'is_vegetarian', e.target.checked)}
                              className="rounded border-stone-300"
                              data-testid={`suggestion-${index}-vegetarian`}
                            />
                            Végétarien
                          </label>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                            <input
                              type="checkbox"
                              checked={suggestionDishes[index]?.is_permanent || false}
                              onChange={(e) => updateSuggestionDish(index, 'is_permanent', e.target.checked)}
                              className="rounded border-stone-300"
                            />
                            Suggestion du moment (visible en permanence)
                          </label>
                          {!suggestionDishes[index]?.is_permanent && (
                            <div className="flex items-center gap-2 pl-4">
                              <label className="text-xs text-stone-600 whitespace-nowrap">Du</label>
                              <input
                                type="date"
                                value={suggestionDishes[index]?.date_from || ''}
                                onChange={(e) => updateSuggestionDish(index, 'date_from', e.target.value)}
                                className="text-xs border border-stone-300 rounded px-1.5 py-0.5"
                              />
                              <label className="text-xs text-stone-600 whitespace-nowrap">au</label>
                              <input
                                type="date"
                                value={suggestionDishes[index]?.valid_until || ''}
                                onChange={(e) => updateSuggestionDish(index, 'valid_until', e.target.value)}
                                className="text-xs border border-stone-300 rounded px-1.5 py-0.5"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveSuggestions} className="flex-1 bg-amber-600 hover:bg-amber-700">
                      <Save className="h-4 w-4 mr-2" /> Enregistrer
                    </Button>
                    <Button variant="outline" onClick={closeSuggestionsEditor}>
                      <X className="h-4 w-4 mr-2" /> Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {getSuggestionsForRestaurant(selectedRestaurant.id).length === 0 ? (
                    <p className="text-stone-400 text-sm italic">Aucune suggestion — ces plats apparaîtront tous les jours sur le site</p>
                  ) : (
                    <div className="space-y-2">
                      {getSuggestionsForRestaurant(selectedRestaurant.id).map((dish) => {
                        const today = new Date().toISOString().split('T')[0];
                        const isExpired = dish.valid_until && dish.valid_until < today;
                        return (
                          <div key={dish.id} className={`flex justify-between items-start p-2 rounded cursor-pointer transition-colors ${isExpired ? 'bg-stone-100 opacity-60' : 'bg-white hover:bg-amber-50'}`}
                            onClick={() => { setEditingSuggestions(true); openSuggestionsEditor(); }}
                          >
                            <div>
                              <p className={`font-medium ${isExpired ? 'text-stone-400 line-through' : 'text-stone-800'}`}>{dish.name}</p>
                              {dish.description && <p className={`text-sm ${isExpired ? 'text-stone-300' : 'text-stone-500'}`}>{dish.description}</p>}
                              {isExpired ? (
                                <p className="text-xs text-red-400 italic font-medium">{"Expirée"} — jusqu'au {new Date(dish.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}</p>
                              ) : !dish.valid_until ? (
                                <p className="text-xs text-amber-600 italic">Suggestion du moment</p>
                              ) : (
                                <p className="text-xs text-amber-600 italic">
                                  Du {new Date(dish.date + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })} au {new Date(dish.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
                                </p>
                              )}
                            </div>
                            <span className={`font-semibold whitespace-nowrap ${isExpired ? 'text-stone-400' : 'text-amber-600'}`}>CHF {dish.price}.-</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* === Section Événements === */}
          <Card className="mb-6 border-purple-200 bg-purple-50/30">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-purple-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Événements / Soirées spéciales
                </CardTitle>
                {!editingEvent && (
                  <Button variant="outline" size="sm" onClick={() => openEventEditor()}>
                    <Plus className="h-3 w-3 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="py-3 px-4 pt-0">
              {editingEvent ? (
                <div className="space-y-3 p-3 bg-white rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-purple-700">
                      {editingEvent === 'new' ? 'Nouvel événement' : 'Modifier l\'événement'}
                    </p>
                    {/* Toggle Brouillon / Publié */}
                    <button
                      onClick={() => setEventForm({...eventForm, is_active: !eventForm.is_active})}
                      className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                        eventForm.is_active
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-stone-100 text-stone-500 border border-stone-300'
                      }`}
                      data-testid="event-publish-toggle"
                    >
                      {eventForm.is_active ? '✓ Publié' : 'Brouillon'}
                    </button>
                  </div>

                  {/* === Essentiel === */}
                  <Input
                    placeholder="Titre (ex: Soirée Moules-Frites)"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-stone-600">Date</Label>
                      <input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                        className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Prix (CHF, optionnel)</Label>
                      <Input
                        placeholder="Ex: 35"
                        type="number"
                        step="0.5"
                        value={eventForm.price}
                        onChange={(e) => setEventForm({...eventForm, price: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* === Détails (dépliable) === */}
                  <button
                    onClick={() => setEventShowDetails(!eventShowDetails)}
                    className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    <ChevronDown className={`h-3 w-3 transition-transform ${eventShowDetails ? 'rotate-180' : ''}`} />
                    {eventShowDetails ? 'Masquer les détails' : 'Ajouter des détails (horaires, menu, description...)'}
                  </button>

                  {eventShowDetails && (
                    <div className="space-y-3 pl-2 border-l-2 border-purple-200">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-stone-600">Heure début</Label>
                          <input
                            type="time"
                            value={eventForm.start_time}
                            onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})}
                            className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">Heure fin</Label>
                          <input
                            type="time"
                            value={eventForm.end_time}
                            onChange={(e) => setEventForm({...eventForm, end_time: e.target.value})}
                            className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">Date fin</Label>
                          <input
                            type="date"
                            value={eventForm.end_date}
                            onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                            className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                          />
                        </div>
                      </div>
                      <Input
                        placeholder="Description courte (ex: Animation karaoké toute la soirée)"
                        value={eventForm.description}
                        onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                      />
                      <textarea
                        placeholder={"Menu spécial (optionnel) :\nEntrée : ...\nPlat : ...\nDessert : ..."}
                        value={eventForm.menu}
                        onChange={(e) => setEventForm({...eventForm, menu: e.target.value})}
                        rows={3}
                        className="w-full text-sm border border-stone-300 rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      {/* Mise en forme */}
                      <div className="flex items-center gap-3 flex-wrap py-1 px-2 bg-purple-50 rounded border border-purple-200">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                          <input
                            type="checkbox"
                            checked={eventForm.is_title_centered || false}
                            onChange={(e) => setEventForm({...eventForm, is_title_centered: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          Centrer titre
                        </label>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                          <input
                            type="checkbox"
                            checked={eventForm.is_menu_centered || false}
                            onChange={(e) => setEventForm({...eventForm, is_menu_centered: e.target.checked})}
                            className="rounded border-stone-300"
                          />
                          Centrer menu
                        </label>
                        <span className="text-[10px] text-stone-400">|</span>
                        <span className="text-[10px] text-stone-400"><code className="bg-stone-100 px-1 rounded">**mot**</code> = <strong>gras</strong> · <code className="bg-stone-100 px-1 rounded">_mot_</code> = <em>italique</em></span>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveEvent} className="flex-1 bg-purple-600 hover:bg-purple-700">
                      <Save className="h-4 w-4 mr-2" /> Enregistrer
                    </Button>
                    <Button variant="outline" onClick={closeEventEditor}>
                      <X className="h-4 w-4 mr-2" /> Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {getEventsForRestaurant(selectedRestaurant.id).length === 0 ? (
                    <p className="text-stone-400 text-sm italic">Aucun événement — idéal pour les soirées spéciales, Pâques, Saint-Valentin...</p>
                  ) : (
                    <div className="space-y-2">
                      {getEventsForRestaurant(selectedRestaurant.id).map((event) => {
                        const today = new Date().toISOString().split('T')[0];
                        const endDate = event.end_date || event.date;
                        const isExpired = endDate < today;
                        return (
                        <div key={event.id} className={`p-3 rounded-lg border ${isExpired ? 'bg-stone-50 border-stone-200 opacity-60' : 'bg-white border-purple-100'}`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1 cursor-pointer" onClick={() => openEventEditor(event)}>
                              <p className={`font-medium flex items-center gap-2 ${isExpired ? 'text-stone-400 line-through' : 'text-stone-800'}`}>
                                {event.title}
                                {!isExpired && event.is_active === false && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-stone-200 text-stone-500 no-underline">Brouillon</span>
                                )}
                              </p>
                              <p className={`text-xs font-medium ${isExpired ? 'text-stone-400' : 'text-purple-600'}`}>
                                {isExpired && <span className="text-red-400 mr-1">{"(terminé)"}</span>}
                                {new Date(event.date + 'T00:00:00').toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })}
                                {event.end_date && ` \u2192 ${new Date(event.end_date + 'T00:00:00').toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                              </p>
                              {event.description && <p className={`text-sm mt-1 ${isExpired ? 'text-stone-300' : 'text-stone-500'}`}>{event.description}</p>}
                              {event.menu && <p className={`text-sm mt-0.5 italic ${isExpired ? 'text-stone-300' : 'text-stone-600'}`}>{event.menu}</p>}
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              {event.price > 0 && <span className={`font-semibold whitespace-nowrap ${isExpired ? 'text-stone-400' : 'text-purple-600'}`}>CHF {event.price}.-</span>}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEventEditor(event)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteEvent(event.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          {/* Flyer */}
                          <div className="mt-2 flex items-center gap-2">
                            {event.flyer_url ? (
                              <>
                                <a href={`${BACKEND_URL}${event.flyer_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                                  <FileText className="h-3 w-3" /> Voir le flyer
                                </a>
                                <button onClick={() => deleteFlyer(event.id)} className="text-xs text-red-400 hover:text-red-600">Supprimer le flyer</button>
                              </>
                            ) : (
                              <label className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-purple-600 cursor-pointer">
                                <Paperclip className="h-3 w-3" />
                                <span>Ajouter un flyer (PDF, image)</span>
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => {
                                  if (e.target.files[0]) uploadFlyer(event.id, e.target.files[0]);
                                }} />
                              </label>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <h2 className="text-lg font-semibold mb-4">Plats du jour</h2>
          <p className="text-sm text-stone-500 mb-6">Cliquez sur le crayon pour ajouter ou modifier les plats (jusqu&apos;à 3 plats par jour)</p>
          
          <div className="space-y-3">
            {weekDays.map((day, index) => {
              const effectiveDishesData = getEffectiveDishesForDay(selectedRestaurant.id, day.date);
              const isEditing = editingDay?.date === day.date;
              
              // Vérifier si on doit afficher le séparateur "Semaine prochaine"
              const showNextWeekSeparator = day.isNextWeek && 
                (index === 0 || !weekDays[index - 1]?.isNextWeek);
              
              return (
                <React.Fragment key={day.date}>
                  {/* Séparateur Semaine prochaine */}
                  {showNextWeekSeparator && (
                    <div className="flex items-center gap-3 py-4 mt-4">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
                      <span className="text-amber-600 text-sm font-medium px-3 bg-amber-50 rounded-full py-1">Semaine prochaine</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
                    </div>
                  )}
                  
                  {(() => {
                    // Vérifier si le jour tombe pendant les vacances
                    const isOnVacation = selectedRestaurant.vacation_start && selectedRestaurant.vacation_end &&
                      day.date >= selectedRestaurant.vacation_start && day.date <= selectedRestaurant.vacation_end;
                    
                    if (isOnVacation) {
                      return (
                        <Card data-day-date={day.date} className="opacity-50 border-dashed bg-stone-50">
                          <CardHeader className="py-3 px-4">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base font-medium capitalize text-stone-400">{day.label}</CardTitle>
                              <span className="text-xs bg-stone-400 text-white px-2 py-0.5 rounded-full">Vacances</span>
                            </div>
                          </CardHeader>
                          <CardContent className="py-2 px-4 pt-0">
                            <p className="text-stone-400 text-sm italic">{selectedRestaurant.vacation_reason || `Fermé (vacances du ${new Date(selectedRestaurant.vacation_start + 'T12:00:00').toLocaleDateString('fr-CH', {day: 'numeric', month: 'long'})} au ${new Date(selectedRestaurant.vacation_end + 'T12:00:00').toLocaleDateString('fr-CH', {day: 'numeric', month: 'long'})})`}</p>
                          </CardContent>
                        </Card>
                      );
                    }

                    return (
                      <Card 
                        data-day-date={day.date}
                        className={`${isEditing ? 'ring-2 ring-amber-500' : ''} ${day.isNextWeek ? 'opacity-80 border-dashed' : ''} ${day.isToday ? 'ring-2 ring-green-500 bg-green-50/50' : ''} ${!isEditing && effectiveDishesData.length === 1 && effectiveDishesData[0]?.name === 'Fermé' ? 'opacity-50 bg-stone-100 border-stone-300' : ''}`}
                      >
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium capitalize flex items-center gap-2">
                        {day.label}
                        {day.isToday && (
                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Aujourd'hui</span>
                        )}
                        {!isEditing && effectiveDishesData.length === 1 && effectiveDishesData[0]?.name === 'Fermé' && (
                          <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">Fermé</span>
                        )}
                      </CardTitle>
                      {!isEditing && (
                        <div className="flex gap-2">
                          {/* Si le jour est marqué fermé : bouton Rouvrir uniquement */}
                          {effectiveDishesData.length === 1 && effectiveDishesData[0]?.name === 'Fermé' ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const fermeId = effectiveDishesData[0]?.id || effectiveDishesData[0]?._id;
                                  if (fermeId) {
                                    await axios.delete(`${API}/dishes/${fermeId}`);
                                  }
                                  toast.success('Jour rouvert');
                                  setDishes(prev => prev.filter(d => d.id !== fermeId && d._id !== fermeId));
                                } catch { toast.error('Erreur'); }
                              }}
                            >
                              Rouvrir
                            </Button>
                          ) : (
                            <>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openDayEditor(day); }}>
                                <Pencil className="h-3 w-3 mr-1" /> {effectiveDishesData.length > 0 ? 'Modifier' : 'Ajouter'}
                              </Button>
                              {effectiveDishesData.length === 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-red-500 border-red-300 hover:bg-red-50"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await axios.post(`${API}/dishes`, {
                                        name: 'Fermé',
                                        restaurant_id: selectedRestaurant.id,
                                        date: day.date,
                                        description: '',
                                        price: 0,
                                        is_suggestion: false,
                                      });
                                      toast.success('Jour marqué fermé');
                                      const newDish = res.data;
                                      if (!newDish.id && newDish._id) newDish.id = newDish._id;
                                      setDishes(prev => [...prev, newDish]);
                                    } catch { toast.error('Erreur'); }
                                  }}
                                >
                                  Fermé
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="py-3 px-4 pt-0">
                    {/* Si fermé, afficher un message clair au lieu de "Aucun plat" */}
                    {!isEditing && effectiveDishesData.length === 1 && effectiveDishesData[0]?.name === 'Fermé' ? (
                      <p className="text-sm text-stone-400 italic">Fermeture exceptionnelle</p>
                    ) : isEditing ? (
                      <div className="space-y-4">
                        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                        {[0, 1, 2].map(index => (
                          <div key={`dish-${index}`} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-amber-700">Plat {index + 1}</p>
                              <div className="flex items-center gap-1">
                                {dayDishes[index]?.name?.trim() && (
                                  <Button size="sm" variant="ghost" onClick={() => {
                                    const newDishes = [...dayDishes];
                                    newDishes[index] = { name: '', description: '', price: '', valid_until: '', is_vegetarian: false, is_menu: false, is_evening: false, is_both_services: false, is_centered: false, is_title_centered: false, is_description_centered: false, flyer_url: null, isInherited: false };
                                    setDayDishes(newDishes);
                                  }} className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" data-testid={`dish-${index}-delete`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                                {dayDishes[index]?.name?.trim() && (
                                  <Button size="sm" onClick={saveDayDishes} className="h-7 px-2 bg-amber-600 hover:bg-amber-700 text-xs">
                                    <Save className="h-3 w-3 mr-1" /> Enregistrer
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="grid gap-2">
                              <textarea
                                placeholder="Nom du plat"
                                value={dayDishes[index]?.name || ''}
                                onChange={(e) => updateDayDish(index, 'name', e.target.value)}
                                autoComplete="off"
                                name={`dish-name-${index}`}
                                rows={1}
                                className="w-full text-sm border border-stone-300 rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                              <div>
                                <textarea
                                  placeholder="Description / accompagnements"
                                  value={dayDishes[index]?.description || ''}
                                  onChange={(e) => updateDayDish(index, 'description', e.target.value)}
                                  autoComplete="off"
                                  name={`dish-desc-${index}`}
                                  rows={dayDishes[index]?.is_menu ? 5 : 1}
                                  className="w-full text-sm border border-stone-300 rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                                />
                                {dayDishes[index]?.is_menu && (
                                  <p className="text-[10px] text-stone-400 mt-0.5">Astuce : <code className="bg-stone-100 px-1 rounded">**mot**</code> = <strong>gras</strong> · <code className="bg-stone-100 px-1 rounded">_mot_</code> = <em>italique</em></p>
                                )}
                              </div>
                              <Input
                                placeholder="Prix (CHF) - laisser vide pour un message"
                                type="number"
                                step="0.5"
                                value={dayDishes[index]?.price || ''}
                                onChange={(e) => updateDayDish(index, 'price', e.target.value)}
                                autoComplete="off"
                                name={`dish-price-${index}`}
                              />
                              {/* Barre de mise en forme — toujours visible */}
                              <div className="flex items-center gap-3 flex-wrap mt-1 py-1 px-2 bg-stone-50 rounded border border-stone-200">
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                                  <input
                                    type="checkbox"
                                    checked={dayDishes[index]?.is_title_centered || false}
                                    onChange={(e) => updateDayDish(index, 'is_title_centered', e.target.checked)}
                                    className="rounded border-stone-300"
                                  />
                                  {"Centrer titre"}
                                </label>
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                                  <input
                                    type="checkbox"
                                    checked={dayDishes[index]?.is_description_centered || false}
                                    onChange={(e) => updateDayDish(index, 'is_description_centered', e.target.checked)}
                                    className="rounded border-stone-300"
                                  />
                                  {"Centrer description"}
                                </label>
                                <span className="text-[10px] text-stone-400">|</span>
                                <span className="text-[10px] text-stone-400"><code className="bg-stone-100 px-1 rounded">**mot**</code> = <strong>gras</strong> · <code className="bg-stone-100 px-1 rounded">_mot_</code> = <em>italique</em></span>
                              </div>
                              {/* Options : végétarien, menu fête */}
                              <div className="flex items-center gap-3 mt-1">
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                                  <input
                                    type="checkbox"
                                    checked={dayDishes[index]?.is_vegetarian || false}
                                    onChange={(e) => updateDayDish(index, 'is_vegetarian', e.target.checked)}
                                    className="rounded border-stone-300"
                                  />
                                  <Leaf className="h-3.5 w-3.5 text-green-600" />
                                  {"Végétarien"}
                                </label>
                                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                                  <input
                                    type="checkbox"
                                    checked={dayDishes[index]?.is_menu || false}
                                    onChange={(e) => updateDayDish(index, 'is_menu', e.target.checked)}
                                    className="rounded border-stone-300"
                                  />
                                  <PartyPopper className="h-3.5 w-3.5 text-amber-600" />
                                  {"Menu fête"}
                                </label>
                                {dayDishes[index]?.is_menu && (
                                  <div className="flex items-center gap-3 text-xs text-stone-600">
                                    <span className="font-medium">Service :</span>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`service-${editingDay?.date}-${index}`}
                                        checked={!dayDishes[index]?.is_evening && !dayDishes[index]?.is_both_services}
                                        onChange={() => { updateDayDish(index, 'is_evening', false); updateDayDish(index, 'is_both_services', false); }}
                                        className="border-stone-300"
                                      />
                                      Midi
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`service-${editingDay?.date}-${index}`}
                                        checked={dayDishes[index]?.is_evening && !dayDishes[index]?.is_both_services}
                                        onChange={() => { updateDayDish(index, 'is_evening', true); updateDayDish(index, 'is_both_services', false); }}
                                        className="border-stone-300"
                                      />
                                      Soir
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`service-${editingDay?.date}-${index}`}
                                        checked={dayDishes[index]?.is_both_services || false}
                                        onChange={() => { updateDayDish(index, 'is_evening', false); updateDayDish(index, 'is_both_services', true); }}
                                        className="border-stone-300"
                                      />
                                      Midi et Soir
                                    </label>
                                  </div>
                                )}
                                {dayDishes[index]?.is_menu && (
                                  <>
                                    {/* Upload flyer menu fête */}
                                    <div className="flex items-center gap-2">
                                      {dayDishes[index]?.flyer_url ? (
                                        <>
                                          <a href={`${API}${dayDishes[index].flyer_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-amber-700 hover:underline">
                                            <Paperclip className="h-3.5 w-3.5" /> Flyer joint
                                          </a>
                                          <button type="button" className="text-xs text-red-500 hover:underline" onClick={async () => {
                                            if (dayDishes[index]?.id) {
                                              await axios.delete(`${API}/dishes/${dayDishes[index].id}/flyer`);
                                              updateDayDish(index, 'flyer_url', null);
                                            }
                                          }}>Supprimer</button>
                                        </>
                                      ) : (
                                        <label className="flex items-center gap-1 text-xs cursor-pointer text-amber-700 hover:underline">
                                          <Paperclip className="h-3.5 w-3.5" />
                                          Joindre un flyer
                                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file || !dayDishes[index]?.id) {
                                              alert('Veuillez d\'abord enregistrer le plat avant de joindre un flyer.');
                                              return;
                                            }
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            const res = await axios.post(`${API}/dishes/${dayDishes[index].id}/flyer`, formData);
                                            updateDayDish(index, 'flyer_url', res.data.flyer_url);
                                          }} />
                                        </label>
                                      )}
                                    </div>
                                  </>
                                )}
                                <span className="text-stone-300">|</span>
                                <label className="text-xs text-stone-600 whitespace-nowrap">Jusqu'au</label>
                                <input
                                  type="date"
                                  value={dayDishes[index]?.valid_until || ''}
                                  onChange={(e) => updateDayDish(index, 'valid_until', e.target.value)}
                                  className="text-xs border border-stone-300 rounded px-1.5 py-0.5"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="flex gap-2 pt-2">
                          <Button variant="outline" onClick={closeDayEditor} className="flex-1">
                            <X className="h-4 w-4 mr-2" /> Fermer
                          </Button>
                          <Button onClick={saveDayDishes} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="save-day-dishes-global">
                            <Save className="h-4 w-4 mr-2" /> Enregistrer
                          </Button>
                        </div>
                        </form>
                      </div>
                    ) : (
                      <div>
                        {effectiveDishesData.length === 0 ? (
                          <p className="text-stone-400 text-sm italic">Aucun plat</p>
                        ) : (
                          <div className="space-y-2">
                            {effectiveDishesData.map((dish, di) => (
                              <div key={di} className={`flex justify-between items-start p-2 rounded ${
                                dish.isInherited 
                                  ? 'bg-stone-100/60 border border-dashed border-stone-300 opacity-50' 
                                  : 'bg-stone-50'
                              }`}>
                                <div>
                                  <p className={`font-medium flex items-center gap-1.5 ${dish.isInherited ? 'text-stone-500' : 'text-stone-800'}`}>
                                    {dish.name}
                                    {dish.is_vegetarian && <Leaf className={`h-3.5 w-3.5 ${dish.isInherited ? 'text-green-400' : 'text-green-600'}`} />}
                                    {dish.is_menu && <PartyPopper className={`h-3.5 w-3.5 ${dish.isInherited ? 'text-amber-400' : 'text-amber-600'}`} />}
                                  </p>
                                  {dish.description && <p className={`text-sm ${dish.isInherited ? 'text-stone-400' : 'text-stone-500'}`}>{dish.description}</p>}
                                  {dish.valid_until && <p className={`text-xs italic ${dish.isInherited ? 'text-stone-400' : 'text-amber-600'}`}>Jusqu'au {new Date(dish.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}</p>}
                                </div>
                                <span className={`font-semibold whitespace-nowrap ${dish.isInherited ? 'text-stone-400' : 'text-amber-600'}`}>CHF {dish.price}.-</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Suggestions actives (lecture seule, grisées) */}
                        {(() => {
                          const activeSuggestions = getSuggestionsForRestaurant(selectedRestaurant.id)
                            .filter(s => !s.valid_until || s.valid_until >= day.date);
                          if (activeSuggestions.length === 0) return null;
                          return (
                            <div className="mt-2 pt-2 border-t border-dashed border-stone-200">
                              <p className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Suggestions en cours</p>
                              {activeSuggestions.map((s) => (
                                <div key={s.id} className="flex justify-between items-start p-2 bg-stone-100/60 rounded opacity-50 pointer-events-none">
                                  <div>
                                    <p className="font-medium text-stone-500 flex items-center gap-1.5 text-sm">
                                      {s.name}
                                      {s.is_vegetarian && <Leaf className="h-3 w-3 text-green-400" />}
                                    </p>
                                    {s.description && <p className="text-xs text-stone-400">{s.description}</p>}
                                    {s.valid_until && <p className="text-[10px] text-stone-400 italic">Jusqu'au {new Date(s.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}</p>}
                                  </div>
                                  {s.price > 0 && <span className="text-stone-400 font-semibold text-sm whitespace-nowrap">CHF {s.price}.-</span>}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
                    );
                  })()}
              </React.Fragment>
              );
            })}
          </div>

          {/* === Section Historique des plats === */}
          <Card className="mt-6 border-stone-300 bg-stone-50">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-semibold text-stone-700 flex items-center gap-2">
                <History className="h-4 w-4" /> Historique des plats
              </CardTitle>
            </CardHeader>
            <CardContent className="py-3 px-4 pt-0">
              {(() => {
                if (dishHistory.length === 0) {
                  return <p className="text-stone-400 text-sm italic">Aucun historique disponible</p>;
                }
                
                // Grouper par semaine (lundi au vendredi)
                const weeks = {};
                dishHistory.forEach(dish => {
                  const d = new Date(dish.date + 'T12:00:00');
                  const monday = new Date(d);
                  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
                  const weekKey = monday.toISOString().split('T')[0];
                  if (!weeks[weekKey]) weeks[weekKey] = [];
                  weeks[weekKey].push(dish);
                });
                
                const sortedWeeks = Object.keys(weeks).sort((a, b) => b.localeCompare(a));
                const lastWeek = sortedWeeks[0];
                const olderWeeks = sortedWeeks.slice(1);
                const dayNames = ['dim','lun','mar','mer','jeu','ven','sam'];
                
                const renderWeekDishes = (weekKey) => {
                  const weekDishes = weeks[weekKey];
                  const mondayDate = new Date(weekKey + 'T12:00:00');
                  const friday = new Date(mondayDate);
                  friday.setDate(mondayDate.getDate() + 4);
                  const label = `${mondayDate.getDate()}/${mondayDate.getMonth()+1} - ${friday.getDate()}/${friday.getMonth()+1}`;
                  
                  // Grouper par date dans la semaine
                  const byDate = {};
                  weekDishes.forEach(d => {
                    if (!byDate[d.date]) byDate[d.date] = [];
                    byDate[d.date].push(d);
                  });
                  
                  return (
                    <div key={weekKey} className="mb-3">
                      <p className="text-xs font-semibold text-stone-500 mb-1.5">Semaine du {label}</p>
                      <div className="space-y-1">
                        {Object.keys(byDate).sort().map(date => {
                          const dateObj = new Date(date + 'T12:00:00');
                          const dayLabel = dayNames[dateObj.getDay()] + ' ' + dateObj.getDate() + '/' + (dateObj.getMonth()+1);
                          return byDate[date].map((dish, i) => (
                            <div key={`${date}-${i}`} className="flex items-center justify-between p-1.5 bg-white rounded border border-stone-200 text-sm">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs text-stone-400 w-14 flex-shrink-0">{dayLabel}</span>
                                <span className="text-stone-700 truncate flex items-center gap-1">
                                  {dish.name}
                                  {dish.is_vegetarian && <Leaf className="h-3 w-3 text-green-600 flex-shrink-0" />}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <span className="text-amber-600 font-medium text-xs">{dish.price > 0 ? `${dish.price} CHF` : ''}</span>
                                <button 
                                  onClick={() => copyHistoryDish(dish)}
                                  className="p-1 rounded hover:bg-stone-100 text-stone-400 hover:text-amber-600 transition-colors"
                                  title="Copier ce plat"
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                    </div>
                  );
                };
                
                return (
                  <div>
                    <button
                      onClick={() => setShowFullHistory(!showFullHistory)}
                      className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 mb-2"
                      data-testid="toggle-history-btn"
                    >
                      <History className="h-3 w-3" />
                      {showFullHistory ? 'Masquer l\'historique' : `Voir la semaine précédente`}
                    </button>
                    {showFullHistory && (
                      <div>
                        {lastWeek && renderWeekDishes(lastWeek)}
                        {olderWeeks.length > 0 && olderWeeks.map(wk => renderWeekDishes(wk))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </main>

        {/* Dialog Restaurant */}
        <Dialog open={isRestaurantDialogOpen} onOpenChange={setIsRestaurantDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRestaurant ? 'Modifier le restaurant' : 'Nouveau restaurant'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveRestaurant} className="space-y-4">
              <div>
                <Label htmlFor="name">Nom</Label>
                <Input id="name" name="name" defaultValue={editingRestaurant?.name} required />
              </div>
              {/* Options rapides sous le nom */}
              <div className="flex items-center gap-4 flex-wrap py-1">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                  <input type="checkbox" name="show_as_new" defaultChecked={editingRestaurant?.show_as_new} className="w-3.5 h-3.5 text-red-500 rounded border-stone-300" />
                  <span>Nouveau</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                  <input type="checkbox" name="suggestions_only" defaultChecked={editingRestaurant?.suggestions_only} className="w-3.5 h-3.5 text-stone-600 rounded border-stone-300" />
                  <span>Suggestions uniquement</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                  <input type="checkbox" name="has_daily_dish" defaultChecked={editingRestaurant?.has_daily_dish} className="w-3.5 h-3.5 text-blue-600 rounded border-stone-300" />
                  <span>Plat du jour quotidien</span>
                </label>
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" name="phone" defaultValue={editingRestaurant?.phone} required />
              </div>
              <div>
                <Label htmlFor="address">Adresse</Label>
                <Input id="address" name="address" defaultValue={editingRestaurant?.address} required />
              </div>
              <div>
                <Label htmlFor="commune">Commune</Label>
                <Input id="commune" name="commune" placeholder="ex: Nyon, Versoix, Coppet..." defaultValue={editingRestaurant?.commune} required />
              </div>
              <div>
                <Label htmlFor="region">Région (pour le filtre "Par lieu")</Label>
                <Input id="region" name="region" placeholder="ex: Terre Sainte, Nyon & région..." defaultValue={editingRestaurant?.region} />
                <p className="text-xs text-stone-400 mt-1">Si vide, la commune sera utilisée dans le filtre</p>
              </div>
              <div>
                <Label htmlFor="website">Site web (optionnel)</Label>
                <Input id="website" name="website" placeholder="https://www.example.ch" defaultValue={editingRestaurant?.website} />
              </div>
              <div>
                <Label htmlFor="menu_url">Lien page plats du jour (optionnel)</Label>
                <Input id="menu_url" name="menu_url" placeholder="https://www.example.ch/plats-du-jour" defaultValue={editingRestaurant?.menu_url} />
              </div>
              <div>
                <Label className="mb-2 block">Jours de fermeture</Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 0, label: 'Lun' },
                    { value: 1, label: 'Mar' },
                    { value: 2, label: 'Mer' },
                    { value: 3, label: 'Jeu' },
                    { value: 4, label: 'Ven' },
                    { value: 5, label: 'Sam' },
                    { value: 6, label: 'Dim' }
                  ].map(day => (
                    <label key={day.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input 
                        type="checkbox" 
                        name="closed_days" 
                        value={day.value}
                        defaultChecked={editingRestaurant?.closed_days?.includes(day.value)}
                        className="w-4 h-4 text-amber-600 rounded border-stone-300"
                      />
                      <span>{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Options restaurant */}
              <div>
                <Label className="mb-2 block">Options</Label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <select name="dogs_allowed" defaultValue={editingRestaurant?.dogs_allowed || ''} className="text-sm border rounded px-2 py-1">
                      <option value="">🐕 Non</option>
                      <option value="terrasse">🐕 Terrasse</option>
                      <option value="interieur">🐕 Intérieur</option>
                      <option value="both">🐕 Les deux</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="checkbox" name="vegetarian_menu" defaultChecked={editingRestaurant?.vegetarian_menu} className="w-4 h-4 text-green-600 rounded border-stone-300" />
                    <span>🥬 Végétarien</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="checkbox" name="takeaway" defaultChecked={editingRestaurant?.takeaway} className="w-4 h-4 text-amber-600 rounded border-stone-300" />
                    <span>📦 Takeaway</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lat">Latitude</Label>
                  <Input id="lat" name="lat" type="number" step="any" defaultValue={editingRestaurant?.lat} />
                </div>
                <div>
                  <Label htmlFor="lng">Longitude</Label>
                  <Input id="lng" name="lng" type="number" step="any" defaultValue={editingRestaurant?.lng} />
                </div>
              </div>

              {/* Options de fermeture */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-stone-700 mb-3">Options de fermeture</h4>
                
                {/* Fermeture temporaire */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="temporary_closure"
                      defaultChecked={editingRestaurant?.temporary_closure || false}
                      className="w-4 h-4 rounded border-stone-300"
                    />
                    <span className="text-sm">Fermeture temporaire</span>
                    <span className="text-xs text-stone-400">(affiche &quot;Plus d&apos;infos sur le site du restaurant&quot;)</span>
                  </label>
                </div>

                {/* Fermeture vacances */}
                <div className="bg-stone-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-stone-600 mb-2">Fermeture pour vacances</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="vacation_start" className="text-xs">Du</Label>
                      <Input 
                        id="vacation_start" 
                        name="vacation_start" 
                        type="date" 
                        defaultValue={editingRestaurant?.vacation_start || new Date().toISOString().split('T')[0]} 
                      />
                    </div>
                    <div>
                      <Label htmlFor="vacation_end" className="text-xs">Au</Label>
                      <Input 
                        id="vacation_end" 
                        name="vacation_end" 
                        type="date" 
                        defaultValue={editingRestaurant?.vacation_end || ''} 
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-400 mt-1">Laissez vide si pas de vacances prévues</p>
                  {(editingRestaurant?.vacation_start || editingRestaurant?.vacation_end) && (
                    <button
                      type="button"
                      onClick={() => {
                        document.getElementById('vacation_start').value = '';
                        document.getElementById('vacation_end').value = '';
                        const reasonEl = document.getElementById('vacation_reason_inline');
                        if (reasonEl) reasonEl.value = '';
                      }}
                      className="text-xs text-red-500 hover:text-red-700 mt-1 underline"
                      data-testid="clear-vacation-inline"
                    >
                      Effacer les dates de vacances
                    </button>
                  )}
                  <div className="mt-2">
                    <Label htmlFor="vacation_reason_inline" className="text-xs">Raison (optionnel)</Label>
                    <Input 
                      id="vacation_reason_inline" 
                      name="vacation_reason" 
                      placeholder="Ex: Fermeture pour Pâques, Vacances d'été..."
                      defaultValue={editingRestaurant?.vacation_reason || ''} 
                      data-testid="vacation-reason-inline"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">Enregistrer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main restaurant list view
  return (
    <div className="min-h-screen bg-stone-50">
      {/* EN-TÊTE COMPLET STICKY - tout le haut reste figé */}
      <div className="sticky top-0 z-30 bg-stone-50">
        <header className="bg-white border-b border-stone-200 shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Utensils className="h-7 w-7 text-amber-600" />
                <h1 className="text-xl font-bold text-stone-800">Administration</h1>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setFbPostText(generateFBPost()); setShowFBPostModal(true); }} className="text-blue-600 border-blue-300 hover:bg-blue-50" data-testid="fb-post-btn">
                  📱 Post FB
                </Button>
                <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600">Déconnexion</Button>
                <Button variant="outline" size="sm" onClick={() => window.location.href = '/'}>Retour au site</Button>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 pt-3 pb-2">
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-3">
            <div className="flex justify-between items-center mb-2">
              <div>
                <h2 className="text-lg font-semibold">Restaurants</h2>
                <p className="text-xs text-stone-500">Cliquez sur un restaurant pour gérer ses plats</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowSpecialPages(true)} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="special-pages-btn">
                  <PartyPopper className="h-4 w-4 mr-1" /> Pages fêtes
                </Button>
                <Button size="sm" onClick={() => openRestaurantDialog()} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
            {/* Compteur — aujourd'hui (jusqu'à 13h30) + demain (permanent) */}
            {(() => {
              const before1330 = isBefore1330();
              const todayDate = getTodayDate();
              const tomorrowDate = getTomorrowDate();
              const activeRestaurants = restaurants.filter(r => r.is_active !== false);

              const countForDate = (refDate) => {
                let missing = 0, suggOnly = 0, ok = 0, closed = 0;
                activeRestaurants.forEach(r => {
                  const s = getDishStatusForDate(r.id, refDate, r.closed_days, r.temporary_closure, r.vacation_start, r.vacation_end, r.suggestions_only, r.has_daily_dish);
                  if (s === 'missing' || s === 'missing_daily') missing++;
                  else if (s === 'suggestion_only') suggOnly++;
                  else if (s === 'ok') ok++;
                  else closed++;
                });
                return { missing, suggOnly, ok, closed };
              };

              const todayCounts = before1330 ? countForDate(todayDate) : null;
              const tomorrowCounts = countForDate(tomorrowDate);
              // J+2 (surlendemain)
              const dayAfterDate = (() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().split('T')[0]; })();
              const dayAfterCounts = countForDate(dayAfterDate);
              const todayLabel = new Date(todayDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
              const tomorrowLabel = new Date(tomorrowDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
              const dayAfterLabel = new Date(dayAfterDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

              const CountLine = ({ label, counts }) => (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-stone-900 capitalize">{label} :</span>
                  <span className="text-sm font-bold text-stone-900 underline">{counts.missing} à appeler</span>
                  {counts.suggOnly > 0 && <><span className="text-stone-300">|</span><span className="text-sm text-amber-700">{counts.suggOnly} suggestion{counts.suggOnly > 1 ? 's' : ''}</span></>}
                  <span className="text-stone-300">|</span>
                  <span className="text-sm text-stone-500">{counts.ok} OK</span>
                  <span className="text-stone-300">|</span>
                  <span className="text-sm text-stone-400">{counts.closed} fermé{counts.closed > 1 ? 's' : ''}</span>
                </div>
              );

              return (
                <div className="mb-2">
                  <div className="text-xs text-stone-500 mb-1">{activeRestaurants.length} restaurants actifs</div>
                  {todayCounts && <CountLine label={todayLabel} counts={todayCounts} />}
                  <CountLine label={tomorrowLabel} counts={tomorrowCounts} />
                  <CountLine label={dayAfterLabel} counts={dayAfterCounts} />
                  <div className="flex items-center gap-3 mt-1 text-xs text-stone-400">
                    <span>🐕 {restaurants.filter(r => r.dogs_allowed).length}</span>
                    <span>🥬 {restaurants.filter(r => r.vegetarian_menu || dishes.some(d => d.restaurant_id === r.id && d.is_vegetarian) || suggestions.some(s => s.restaurant_id === r.id && s.is_vegetarian)).length}</span>
                    <span>📦 {restaurants.filter(r => r.takeaway).length}</span>
                  </div>
                </div>
              );
            })()}

            {/* Recherche */}
            <div className="mb-2">
              <Input
                type="text"
                placeholder="Rechercher un restaurant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 text-sm border-stone-300 focus:border-amber-500 focus:ring-amber-500"
                data-testid="restaurant-search"
              />
            </div>

            {/* Tri */}
            <div className="flex items-center gap-3 mb-1.5">
              <span className="text-sm text-stone-600">Tri :</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortMode('missing-first')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    sortMode === 'missing-first' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  À appeler en premier
                </button>
                <button
                  onClick={() => setSortMode('alphabetical')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    sortMode === 'alphabetical' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  Alphabétique (A-Z)
                </button>
                <button
                  onClick={() => setSortMode('by-region')}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    sortMode === 'by-region' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  Par région
                </button>
              </div>
            </div>

            {/* Légende visuelle */}
            <div className="flex items-center gap-3 text-xs text-stone-400 flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-4 h-3 bg-white border-2 border-stone-900 rounded-sm inline-block"></span> À appeler</span>
              <span className="inline-flex items-center gap-1"><span className="w-4 h-3 bg-amber-50 border-2 border-amber-400 rounded-sm inline-block"></span> Suggestion</span>
              <span className="inline-flex items-center gap-1"><span className="w-4 h-3 bg-stone-700 rounded-sm inline-block"></span> OK</span>
              <span className="inline-flex items-center gap-1"><span className="w-4 h-3 bg-stone-200 border border-stone-300 rounded-sm inline-block"></span> Fermé</span>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-4">
        {/* === Dernières saisies (tous restaurants) === */}
        {recentDishes.length > 0 && (
          <div className="mb-4" data-testid="recent-dishes-section">
            <button
              onClick={() => setShowRecentDishes(!showRecentDishes)}
              className="w-full bg-white border border-stone-200 rounded-lg px-4 py-2.5 mb-2 flex items-center justify-between hover:bg-stone-50 transition-colors shadow-sm"
              data-testid="toggle-recent-dishes"
            >
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <h3 className="font-bold text-sm text-stone-800">Dernières saisies</h3>
                <span className="text-xs text-stone-400 font-normal">({recentDishes.length})</span>
              </div>
              <span className={`text-stone-400 transition-transform text-xs ${showRecentDishes ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {showRecentDishes && (
              <div className="bg-white rounded-lg border border-stone-200 shadow-sm overflow-hidden">
                <div className="max-h-[280px] overflow-y-auto divide-y divide-stone-100">
                  {recentDishes.map((dish, idx) => {
                    const updatedAt = dish.updated_at ? new Date(dish.updated_at) : null;
                    const timeAgo = updatedAt ? (() => {
                      const diffMs = Date.now() - updatedAt.getTime();
                      const diffMin = Math.floor(diffMs / 60000);
                      if (diffMin < 1) return "à l'instant";
                      if (diffMin < 60) return `il y a ${diffMin}min`;
                      const diffH = Math.floor(diffMin / 60);
                      if (diffH < 24) return `il y a ${diffH}h`;
                      const diffD = Math.floor(diffH / 24);
                      return `il y a ${diffD}j`;
                    })() : '';
                    const dateLabel = dish.date ? new Date(dish.date + 'T12:00:00').toLocaleDateString('fr-CH', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                    
                    return (
                      <div
                        key={dish._id + '-' + idx}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-amber-50/50 transition-colors group"
                        data-testid={`recent-dish-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-stone-800 truncate">{dish.name}</span>
                            {dish.price > 0 && <span className="text-xs text-amber-600 font-medium flex-shrink-0">{dish.price} CHF</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-stone-400">
                            <span className="font-medium text-stone-600">{dish.restaurant_name || 'Restaurant'}</span>
                            {dish.restaurant_commune && <span>• {dish.restaurant_commune}</span>}
                            <span>• {dateLabel}</span>
                            <span className="text-stone-300">• {timeAgo}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const resto = restaurants.find(r => r.id === dish.restaurant_id);
                            if (resto) {
                              setSelectedRestaurant(resto);
                              setEditingDay(null);
                              setEditingEvent(null);
                              setEditingSuggestions(false);
                              generateWeekDays(resto.closed_days || []);
                              loadDishHistory(resto.id);
                              window.scrollTo(0, 0);
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-md border border-amber-200 hover:bg-amber-100 transition-colors opacity-70 group-hover:opacity-100 flex-shrink-0"
                          data-testid={`recent-dish-edit-${idx}`}
                        >
                          <Pencil className="h-3 w-3" /> Modifier
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === Bouton "Continuer" vers le dernier restaurant modifié === */}
        {lastModifiedRestaurantId && !selectedRestaurant && (() => {
          const lastResto = restaurants.find(r => r.id === lastModifiedRestaurantId);
          if (!lastResto) return null;
          return (
            <div className="mb-3" data-testid="continue-last-modified">
              <button
                onClick={() => {
                  setSelectedRestaurant(lastResto);
                  setEditingDay(null);
                  setEditingEvent(null);
                  setEditingSuggestions(false);
                  generateWeekDays(lastResto.closed_days || []);
                  loadDishHistory(lastResto.id);
                  window.scrollTo(0, 0);
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors text-sm"
              >
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-amber-600" />
                  <span className="text-stone-700">Reprendre la saisie pour <strong className="text-amber-700">{lastResto.name}</strong></span>
                </div>
                <span className="text-xs text-stone-400">Dernier modifié</span>
              </button>
            </div>
          );
        })()}

        <div className="grid gap-2">
          {(() => {
            const todayDate = getTodayDate();
            const tomorrowDate = getTomorrowDate();
            const showToday = isBefore1330();
            const query = searchQuery.toLowerCase().trim();

            const enrichForDate = (refDate) => restaurants.map(restaurant => {
              const status = getDishStatusForDate(restaurant.id, refDate, restaurant.closed_days, restaurant.temporary_closure, restaurant.vacation_start, restaurant.vacation_end, restaurant.suggestions_only, restaurant.has_daily_dish);
              return { restaurant, status };
            });

            const filterBySearch = (items) => {
              if (!query) return items;
              return items.filter(({ restaurant }) =>
                restaurant.name.toLowerCase().includes(query) ||
                (restaurant.commune || '').toLowerCase().includes(query) ||
                (restaurant.region || '').toLowerCase().includes(query)
              );
            };

            const enriched = enrichForDate(tomorrowDate);
            const todayEnriched = showToday ? enrichForDate(todayDate) : [];
            // Pour Alphabétique/Région : combiner aujourd'hui et demain
            // Si un restaurant est fermé aujourd'hui mais "missing" demain, il faut qu'il apparaisse comme "urgent"
            const primaryEnriched = (() => {
              if (!showToday) return enriched;
              // Construire un map des statuts de demain pour lookup rapide
              const tomorrowStatusMap = {};
              enriched.forEach(e => { tomorrowStatusMap[e.restaurant.id] = e.status; });
              // Pour chaque restaurant, prendre le statut le plus urgent entre aujourd'hui et demain
              return todayEnriched.map(item => {
                const tomorrowStatus = tomorrowStatusMap[item.restaurant.id] || item.status;
                const todayStatus = item.status;
                // Si fermé/vacances/temporaire aujourd'hui, utiliser le statut de demain
                if (['closed', 'vacation', 'temporary'].includes(todayStatus)) {
                  return { ...item, status: tomorrowStatus };
                }
                // Si "ok" ou "suggestion_only" aujourd'hui mais "missing" demain, montrer comme "missing"
                if (todayStatus !== 'missing' && tomorrowStatus === 'missing') {
                  return { ...item, status: 'missing' };
                }
                return item;
              });
            })();

            // Carte restaurant avec style selon le statut
            const renderCard = (item, cardStyle) => {
              const { restaurant } = item;
              const isUrgent = cardStyle === 'urgent';
              const isSuggestion = cardStyle === 'suggestion';
              const isClosed = cardStyle === 'closed';
              const isOk = cardStyle === 'ok';
              const isLastModified = restaurant.id === lastModifiedRestaurantId;

              return (
                <Card 
                  key={restaurant.id}
                  ref={isLastModified ? lastModifiedRef : undefined}
                  data-testid={`restaurant-card-${restaurant.id}`}
                  className={`cursor-pointer transition-shadow ${
                    !restaurant.is_active ? 'opacity-50 bg-stone-100 border-stone-200' :
                    isUrgent ? 'bg-white border-2 border-stone-900 shadow-md hover:shadow-lg' :
                    isSuggestion ? 'bg-amber-50 border-2 border-amber-400 shadow-sm hover:shadow-md' :
                    isClosed ? 'bg-stone-200 border-stone-300' :
                    'bg-stone-700 border-stone-700'
                  } ${isLastModified ? 'ring-2 ring-amber-500 ring-offset-2' : ''}`}
                  onClick={() => {
                    setSelectedRestaurant(restaurant);
                    setEditingDay(null);
                    setEditingEvent(null);
                    setEditingSuggestions(false);
                    generateWeekDays(restaurant.closed_days || []);
                    loadDishHistory(restaurant.id);
                    window.scrollTo(0, 0);
                  }}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`text-base ${
                          !restaurant.is_active ? 'text-stone-400' :
                          isUrgent ? 'font-bold text-stone-900' :
                          isSuggestion ? 'font-semibold text-amber-800' :
                          isClosed ? 'text-stone-500 italic' :
                          'text-stone-300'
                        }`}>
                          {(() => {
                            const linkUrl = restaurant.menu_url || restaurant.website;
                            const linkTitle = restaurant.menu_url ? 'Ouvrir la page plats du jour' : 'Ouvrir le site web';
                            if (linkUrl) {
                              return (
                                <a 
                                  href={linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={isUrgent || isSuggestion ? 'hover:text-amber-600 hover:underline' : 'hover:text-amber-300 hover:underline'}
                                  title={linkTitle}
                                >
                                  {restaurant.name}
                                </a>
                              );
                            }
                            return restaurant.name;
                          })()}
                          {isClosed && <span className="ml-2 text-xs font-normal">(fermé)</span>}
                          {!restaurant.is_active && <span className="ml-2 text-xs font-normal">(masqué)</span>}
                          {isLastModified && <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-500 text-white no-underline" data-testid="last-modified-badge">Dernier modifié</span>}
                        </h3>
                        {isSuggestion && <p className="text-xs text-amber-600 italic">Plat du moment uniquement</p>}
                        <p className={`text-xs ${isOk ? 'text-stone-400' : 'text-stone-400'}`}>
                          {restaurant.commune || restaurant.address}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => toggleRestaurantActive(e, restaurant)}
                        className={isOk ? 'text-stone-300 hover:text-white hover:bg-stone-600' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-50'}
                        title={restaurant.is_active ? 'Masquer du site' : 'Afficher sur le site'}
                      >
                        {restaurant.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            };

            // Titre de section — accordéon orange
            const toggleSection = (sectionKey) => {
              setOpenSections(prev => {
                const next = new Set(prev);
                if (next.has(sectionKey)) next.delete(sectionKey);
                else next.add(sectionKey);
                return next;
              });
            };
            const renderSectionAccordion = (title, count, sectionKey, items, cardStyle) => {
              const isOpen = openSections.has(sectionKey);
              return (
                <div key={sectionKey} className="mb-1">
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full bg-amber-600 text-white rounded-lg px-4 py-2 mt-2 mb-1 flex items-center justify-between hover:bg-amber-700 transition-colors"
                  >
                    <h3 className="font-bold text-sm">{title} <span className="font-normal">({count})</span></h3>
                    <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                  </button>
                  {isOpen && (
                    <div className="grid gap-2">
                      {items.map(item => renderCard(item, cardStyle))}
                    </div>
                  )}
                </div>
              );
            };

            // Helpers
            const isStrictMissing = (s) => s === 'missing' || s === 'missing_daily';
            const isSuggestionOnly = (s) => s === 'suggestion_only';
            const isClosedStatus = (s) => ['closed', 'temporary', 'vacation'].includes(s);
            const alphaSort = (a, b) => a.restaurant.name.localeCompare(b.restaurant.name);

            // Séparer masqués des actifs + filtrer par recherche (basé sur la date primaire)
            const activeEnriched = filterBySearch(primaryEnriched.filter(e => e.restaurant.is_active !== false));
            const hiddenEnriched = filterBySearch(primaryEnriched.filter(e => e.restaurant.is_active === false));

            // Mode "Par region" — Accordéon
            if (sortMode === 'by-region') {
              const groups = {};
              activeEnriched.forEach(item => {
                const region = item.restaurant.region || item.restaurant.commune || 'Non défini';
                if (!groups[region]) groups[region] = [];
                groups[region].push(item);
              });
              const toggleRegion = (region) => {
                setOpenRegions(prev => {
                  const next = new Set(prev);
                  if (next.has(region)) next.delete(region);
                  else next.add(region);
                  return next;
                });
              };
              const regionCards = Object.keys(groups).sort((a, b) => a.localeCompare(b)).map(region => {
                const isOpen = openRegions.has(region);
                return (
                  <div key={region} className="mb-1">
                    <button
                      onClick={() => toggleRegion(region)}
                      className="w-full bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-1 flex items-center justify-between hover:bg-amber-100 transition-colors"
                    >
                      <h3 className="font-bold text-stone-800 text-sm">{region} <span className="font-normal text-stone-500">({groups[region].length})</span></h3>
                      <span className={`text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {isOpen && (
                      <div className="grid gap-2 ml-2">
                        {groups[region]
                          .sort(alphaSort)
                          .map(item => {
                            const style = isStrictMissing(item.status) ? 'urgent' :
                              isSuggestionOnly(item.status) ? 'suggestion' :
                              isClosedStatus(item.status) ? 'closed' : 'ok';
                            return renderCard(item, style);
                          })
                        }
                      </div>
                    )}
                  </div>
                );
              });
              if (hiddenEnriched.length > 0) {
                regionCards.push(renderSectionAccordion('MASQUÉS', hiddenEnriched.length, 'hidden-region', hiddenEnriched.sort(alphaSort), 'closed'));
              }
              return regionCards;
            }

            // Mode "Alphabetique" (déjà filtré par activeEnriched)
            if (sortMode === 'alphabetical') {
              const cards = activeEnriched
                .sort(alphaSort)
                .map(item => {
                  const style = isStrictMissing(item.status) ? 'urgent' :
                    isSuggestionOnly(item.status) ? 'suggestion' :
                    isClosedStatus(item.status) ? 'closed' : 'ok';
                  return renderCard(item, style);
                });
              if (hiddenEnriched.length > 0) {
                cards.push(renderSectionAccordion('MASQUÉS', hiddenEnriched.length, 'hidden-alpha', hiddenEnriched.sort(alphaSort), 'closed'));
              }
              return cards;
            }

            // Mode "A appeler en premier" — groupé par statut, puis par date
            const todayActive = showToday ? filterBySearch(todayEnriched.filter(e => e.restaurant.is_active !== false)) : [];
            const tomorrowActive = filterBySearch(enriched.filter(e => e.restaurant.is_active !== false));
            const tomorrowHidden = filterBySearch(enriched.filter(e => e.restaurant.is_active === false));
            
            // J+2 (surlendemain)
            const dayAfterDate = (() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().split('T')[0]; })();
            const dayAfterEnriched = enrichForDate(dayAfterDate);
            const dayAfterActive = filterBySearch(dayAfterEnriched.filter(e => e.restaurant.is_active !== false));

            const todayLabel = showToday ? new Date(todayDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
            const tomorrowLabel = new Date(tomorrowDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            const dayAfterLabel = new Date(dayAfterDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

            const statusGroups = [
              { key: 'urgent', title: 'À APPELER', style: 'urgent', filter: isStrictMissing },
              { key: 'suggestion', title: 'SUGGESTION UNIQUEMENT', style: 'suggestion', filter: isSuggestionOnly },
              { key: 'ok', title: 'OK', style: 'ok', filter: (s) => !isStrictMissing(s) && !isSuggestionOnly(s) && !isClosedStatus(s) },
              { key: 'closed', title: 'FERMÉS', style: 'closed', filter: isClosedStatus },
            ];

            const result = [];
            statusGroups.forEach(({ key, title, style, filter: statusFilter }) => {
              const todayItems = showToday ? todayActive.filter(e => statusFilter(e.status)).sort(alphaSort) : [];
              const tomorrowItems = tomorrowActive.filter(e => statusFilter(e.status)).sort(alphaSort);
              const dayAfterItems = dayAfterActive.filter(e => statusFilter(e.status)).sort(alphaSort);

              if (todayItems.length === 0 && tomorrowItems.length === 0 && dayAfterItems.length === 0) return;

              if (showToday && todayItems.length > 0) {
                result.push(renderSectionAccordion(`${title} — ${todayLabel}`, todayItems.length, `${key}-today`, todayItems, style));
              }
              if (tomorrowItems.length > 0) {
                result.push(renderSectionAccordion(`${title} — ${tomorrowLabel}`, tomorrowItems.length, `${key}-tomorrow`, tomorrowItems, style));
              }
              if (dayAfterItems.length > 0) {
                result.push(renderSectionAccordion(`${title} — ${dayAfterLabel}`, dayAfterItems.length, `${key}-dayafter`, dayAfterItems, style));
              }
            });

            if (tomorrowHidden.length > 0) {
              result.push(renderSectionAccordion('MASQUÉS', tomorrowHidden.length, 'hidden', tomorrowHidden.sort(alphaSort), 'closed'));
            }
            return result;
          })()}
        </div>
      </main>

      {/* Dialog Restaurant */}
      <Dialog open={isRestaurantDialogOpen} onOpenChange={setIsRestaurantDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRestaurant ? 'Modifier le restaurant' : 'Nouveau restaurant'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRestaurant} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom</Label>
              <Input id="name" name="name" defaultValue={editingRestaurant?.name} required />
            </div>
            {/* Options rapides sous le nom */}
            <div className="flex items-center gap-4 flex-wrap py-1">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                <input type="checkbox" name="show_as_new" defaultChecked={editingRestaurant?.show_as_new || false} className="w-3.5 h-3.5 text-red-500 rounded border-stone-300" />
                <span>Nouveau</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                <input type="checkbox" name="suggestions_only" defaultChecked={editingRestaurant?.suggestions_only || false} className="w-3.5 h-3.5 text-stone-600 rounded border-stone-300" />
                <span>Suggestions uniquement</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-stone-600">
                <input type="checkbox" name="has_daily_dish" defaultChecked={editingRestaurant?.has_daily_dish || false} className="w-3.5 h-3.5 text-blue-600 rounded border-stone-300" />
                <span>Plat du jour quotidien</span>
              </label>
            </div>
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" name="phone" defaultValue={editingRestaurant?.phone} required />
            </div>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input id="address" name="address" defaultValue={editingRestaurant?.address} required />
            </div>
            <div>
              <Label htmlFor="commune">Commune</Label>
              <Input id="commune" name="commune" placeholder="ex: Nyon, Versoix, Coppet..." defaultValue={editingRestaurant?.commune} required />
            </div>
            <div>
              <Label htmlFor="region">Région (pour le filtre "Par lieu")</Label>
              <Input id="region" name="region" placeholder="ex: Terre Sainte, Nyon & région..." defaultValue={editingRestaurant?.region} />
              <p className="text-xs text-stone-400 mt-1">Si vide, la commune sera utilisée dans le filtre</p>
            </div>
            <div>
              <Label htmlFor="website">Site web (optionnel)</Label>
              <Input id="website" name="website" placeholder="https://www.example.ch" defaultValue={editingRestaurant?.website} />
            </div>
            <div>
              <Label htmlFor="menu_url">Lien page plats du jour (optionnel)</Label>
              <Input id="menu_url" name="menu_url" placeholder="https://www.example.ch/plats-du-jour" defaultValue={editingRestaurant?.menu_url} />
            </div>
            <div>
              <Label className="mb-2 block">Jours de fermeture</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: 0, label: 'Lun' },
                  { value: 1, label: 'Mar' },
                  { value: 2, label: 'Mer' },
                  { value: 3, label: 'Jeu' },
                  { value: 4, label: 'Ven' },
                  { value: 5, label: 'Sam' },
                  { value: 6, label: 'Dim' }
                ].map(day => (
                  <label key={day.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="closed_days" 
                      value={day.value}
                      defaultChecked={editingRestaurant?.closed_days?.includes(day.value)}
                      className="w-4 h-4 text-amber-600 rounded border-stone-300"
                    />
                    <span>{day.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lat">Latitude</Label>
                <Input id="lat" name="lat" type="number" step="any" defaultValue={editingRestaurant?.lat} />
              </div>
              <div>
                <Label htmlFor="lng">Longitude</Label>
                <Input id="lng" name="lng" type="number" step="any" defaultValue={editingRestaurant?.lng} />
              </div>
            </div>

            {/* Options de fermeture */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-stone-700 mb-3">Options de fermeture</h4>
              
              {/* Fermeture temporaire */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="temporary_closure"
                    defaultChecked={editingRestaurant?.temporary_closure || false}
                    className="w-4 h-4 rounded border-stone-300"
                  />
                  <span className="text-sm">Fermeture temporaire</span>
                  <span className="text-xs text-stone-400">(affiche &quot;Plus d&apos;infos sur le site du restaurant&quot;)</span>
                </label>
              </div>

              {/* Fermeture vacances */}
              <div className="bg-stone-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-stone-600 mb-2">Fermeture pour vacances</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="vacation_start" className="text-xs">Du</Label>
                    <Input 
                      id="vacation_start" 
                      name="vacation_start" 
                      type="date" 
                      defaultValue={editingRestaurant?.vacation_start || ''} 
                    />
                  </div>
                  <div>
                    <Label htmlFor="vacation_end" className="text-xs">Au</Label>
                    <Input 
                      id="vacation_end" 
                      name="vacation_end" 
                      type="date" 
                      defaultValue={editingRestaurant?.vacation_end || ''} 
                    />
                  </div>
                </div>
                <p className="text-xs text-stone-400 mt-1">Laissez vide si pas de vacances prévues</p>
                {(editingRestaurant?.vacation_start || editingRestaurant?.vacation_end) && (
                  <button
                    type="button"
                    onClick={() => {
                      const form = document.querySelector('[data-testid="restaurant-edit-form"]') || document.forms[0];
                      const vs = form?.querySelector('[name="vacation_start"]');
                      const ve = form?.querySelector('[name="vacation_end"]');
                      const vr = form?.querySelector('[name="vacation_reason"]');
                      if (vs) vs.value = '';
                      if (ve) ve.value = '';
                      if (vr) vr.value = '';
                    }}
                    className="text-xs text-red-500 hover:text-red-700 mt-1 underline"
                    data-testid="clear-vacation-dialog"
                  >
                    Effacer les dates de vacances
                  </button>
                )}
                <div className="mt-2">
                  <Label htmlFor="vacation_reason_dialog" className="text-xs">Raison (optionnel)</Label>
                  <Input 
                    id="vacation_reason_dialog" 
                    name="vacation_reason" 
                    placeholder="Ex: Fermeture pour Pâques, Vacances d'été..."
                    defaultValue={editingRestaurant?.vacation_reason || ''} 
                    data-testid="vacation-reason-dialog"
                  />
                </div>
              </div>
            </div>

            {/* Note menu personnalisée */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-stone-700 mb-2">Note menu (optionnel)</h4>
              <Input 
                id="menu_note" 
                name="menu_note" 
                placeholder="Ex: Menu valable 15 jours, Suggestions de la semaine..."
                defaultValue={editingRestaurant?.menu_note || ''} 
              />
              <p className="text-xs text-stone-400 mt-1">Si vide, affiche la date de dernière mise à jour</p>
            </div>

            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">Enregistrer</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Pages Spéciales */}
      <Dialog open={showSpecialPages} onOpenChange={setShowSpecialPages}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PartyPopper className="h-5 w-5 text-amber-600" />
              Pages Spéciales (fêtes)
            </DialogTitle>
          </DialogHeader>

          {/* Formulaire de création/édition */}
          {editingSpecialPage ? (
            <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="special-page-form">
              <p className="text-sm font-semibold text-amber-700">
                {editingSpecialPage === 'new' ? 'Nouvelle page spéciale' : 'Modifier la page'}
              </p>
              <div>
                <Label className="text-xs text-stone-600">Titre</Label>
                <Input
                  placeholder="ex: Menu de Pâques 2026"
                  value={specialPageForm.title}
                  onChange={(e) => setSpecialPageForm({...specialPageForm, title: e.target.value})}
                  data-testid="special-page-title"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Slug (URL) — auto-généré si vide</Label>
                <Input
                  placeholder="ex: paques-2026"
                  value={specialPageForm.slug}
                  onChange={(e) => setSpecialPageForm({...specialPageForm, slug: e.target.value})}
                  data-testid="special-page-slug"
                />
                <p className="text-xs text-stone-400 mt-1">URL finale : /fete/{specialPageForm.slug || 'auto'}</p>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Texte d'introduction (affiché sous le titre)</Label>
                <textarea
                  placeholder="Ex : Découvrez les menus de Pâques proposés par nos restaurants partenaires..."
                  value={specialPageForm.description}
                  onChange={(e) => setSpecialPageForm({...specialPageForm, description: e.target.value})}
                  rows={3}
                  className="w-full text-sm border border-stone-300 rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="special-page-description"
                />
                <p className="text-[10px] text-stone-400 mt-0.5">Astuce : <code className="bg-stone-100 px-1 rounded">**mot**</code> = <strong>gras</strong> · <code className="bg-stone-100 px-1 rounded">_mot_</code> = <em>italique</em></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-600">Visible dès le</Label>
                  <input
                    type="date"
                    value={specialPageForm.start_date}
                    onChange={(e) => setSpecialPageForm({...specialPageForm, start_date: e.target.value})}
                    className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                    data-testid="special-page-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Visible jusqu'au</Label>
                  <input
                    type="date"
                    value={specialPageForm.end_date}
                    onChange={(e) => setSpecialPageForm({...specialPageForm, end_date: e.target.value})}
                    className="w-full text-sm border border-stone-300 rounded px-2 py-1.5"
                    data-testid="special-page-end-date"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={saveSpecialPage} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="save-special-page-btn">
                  <Save className="h-4 w-4 mr-2" /> Enregistrer
                </Button>
                <Button variant="outline" onClick={closeSpecialPageEditor}>
                  <X className="h-4 w-4 mr-2" /> Annuler
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" onClick={() => openSpecialPageEditor()} className="bg-amber-600 hover:bg-amber-700" data-testid="add-special-page-btn">
              <Plus className="h-4 w-4 mr-1" /> Nouvelle page
            </Button>
          )}

          {/* Liste des pages spéciales existantes */}
          <div className="space-y-3 mt-2">
            {specialPages.length === 0 ? (
              <p className="text-stone-400 text-sm italic text-center py-4">
                Aucune page spéciale. Créez-en une pour les fêtes (Pâques, Noël...).
              </p>
            ) : (
              specialPages.map((page) => {
                const today = new Date().toISOString().split('T')[0];
                const isActive = page.is_active && page.start_date <= today && page.end_date >= today;
                const isPast = page.end_date < today;
                return (
                  <div
                    key={page.id}
                    className={`p-3 rounded-lg border ${isPast ? 'bg-stone-50 border-stone-200 opacity-60' : isActive ? 'bg-green-50 border-green-300' : 'bg-white border-amber-200'}`}
                    data-testid={`special-page-card-${page.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold ${isPast ? 'text-stone-400 line-through' : 'text-stone-800'}`}>
                            {page.title}
                          </p>
                          {isActive && (
                            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Active</span>
                          )}
                          {!page.is_active && (
                            <span className="text-xs bg-stone-400 text-white px-2 py-0.5 rounded-full">Inactive</span>
                          )}
                        </div>
                        <p className="text-xs text-stone-500 mt-0.5">
                          /fete/{page.slug} — du {new Date(page.start_date + 'T12:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })} au {new Date(page.end_date + 'T12:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
                        </p>
                        {/* Banner upload */}
                        <div className="flex items-center gap-2 mt-2">
                          {page.banner_data || page.banner_mime_type ? (
                            <span className="text-xs text-green-600 flex items-center gap-1"><Check className="h-3 w-3" /> Bannière uploadée</span>
                          ) : (
                            <label className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 cursor-pointer" data-testid={`upload-banner-${page.id}`}>
                              <Paperclip className="h-3 w-3" />
                              <span>Ajouter une bannière</span>
                              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => {
                                if (e.target.files[0]) uploadSpecialPageBanner(page.id, e.target.files[0]);
                              }} />
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleSpecialPageActive(page)} title={page.is_active ? 'Désactiver' : 'Activer'}>
                          {page.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSpecialPageEditor(page)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteSpecialPage(page.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Instructions */}
          <div className="bg-stone-50 rounded-lg p-3 mt-2 text-xs text-stone-500">
            <p className="font-medium text-stone-600 mb-1">Comment ça marche ?</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Créez une page spéciale (ex: "Pâques 2026")</li>
              <li>Ajoutez des plats avec la case "Menu fête" cochée dans chaque restaurant</li>
              <li>Les plats "Menu fête" dans la période apparaîtront sur la page /fete/slug</li>
              <li>Cochez "Aussi le soir" pour auto-créer un événement soirée</li>
            </ol>
          </div>
        </DialogContent>
      </Dialog>

      {/* === Modale Générateur Post Facebook === */}
      <Dialog open={showFBPostModal} onOpenChange={setShowFBPostModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📱 Générateur de Post Facebook
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-500">Modifiez le texte si besoin, puis copiez-le pour le coller sur votre page Facebook.</p>
            <textarea
              value={fbPostText}
              onChange={(e) => setFbPostText(e.target.value)}
              className="w-full h-40 p-3 border border-stone-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="fb-post-textarea"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFbPostText(generateFBPost())}
                className="flex-1"
                data-testid="fb-post-refresh"
              >
                🔄 Autre proposition
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(fbPostText);
                  toast.success('Texte copié !');
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="fb-post-copy"
              >
                <Copy className="h-4 w-4 mr-1" /> Copier
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
